import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  LuCheck,
  LuChevronDown,
  LuHandCoins,
  LuImage,
  LuLibrary,
  LuPause,
  LuPenLine,
  LuPlay,
  LuRefreshCw,
  LuSearch,
  LuX,
  LuZoomIn,
} from "react-icons/lu";
import AppModal from "../common/modal/AppModal";
import Lightbox from "../merlin/Lightbox";
import i18n from "i18next";
import { getToken } from "../../../common/environment_config.es6";
import { useHtmlByUrl } from "../../../common/html_artifact_store.es6";
import { fetchIdealStudioFiles, approveJob } from "../../../common/server_utils.es6";
import { waitForJob } from "../../../common/job_poll.es6";
import { onJobsSettled } from "../../utils/jobSettled";
import { withSidecarSuffix, credentialsFor } from "../../../common/utils.es6";
import { isFileTooLarge, fileTooLargeMessage } from "../../../common/upload_limits.es6";
import { pickSampleLanguage } from "../../utils/sampleVoiceLanguage";
import ImagePromptLibraryService from "../../services/imagePromptLibraryService";
import { VISIBLE_IMAGE_STYLE_PROVIDER_OPTIONS } from "../image_style_library/imageStyleProviderOptions";
import { usePopupPrefill } from "../ai-chat/aiPopupGateway";
import { isCreditRefusal, notifyInsufficientBalance } from "../../utils/insufficientBalance";
import SceneAudioService from "../../services/sceneAudioService";
import { motionSceneEligibility, blockerLabel } from "../../utils/motionSceneEligibility";
import "./_infographicSceneModal.scss";
import { pickContentLocale } from "../../../common/content_language";

/**
 * The models offered under "Özel" on the motion tab.
 *
 * These are LLMs, not image providers: the motion tab's output is code, so the
 * provider chips the other tabs show (which pick an image generator) would mean
 * nothing here. Ids match the middleware's catalog — GPT-5.6 is deployed as
 * gpt-5.6-sol there.
 */
const MOTION_MODEL_OPTIONS = [
  { value: "claude-opus-5.5", label: "Claude Opus 5.5" },
  { value: "gpt-6", label: "GPT-6" },
  { value: "gpt-6.1", label: "GPT-6.1" },
];

const API_SERVICE_MAP = { azure: "microsoft", elevenlabs: "elevenlabs", google: "gemini" };
const VOICE_SERVICE_TYPE_MAP = { azure: 0, elevenlabs: 1, google: 2 };
const SERVICE_NORMALIZE_MAP = {
  microsoft: "azure",
  azure: "azure",
  elevenlabs: "elevenlabs",
  gemini: "google",
  google: "google",
};

// Ses cinsiyetini ("Male"/"female"/"M"/0 vb.) normalize eder (AIVoiceTab ile aynı).
const normalizeGender = (g) => {
  const v = String(g ?? "")
    .toLowerCase()
    .trim();
  if (v === "male" || v === "m" || v === "0" || v === "erkek") return "male";
  if (v === "female" || v === "f" || v === "1" || v === "kadın" || v === "kadin") return "female";
  return "";
};

// Ses kartı avatarının cinsiyete göre rengi (AIVoiceTab ile aynı): erkek mavi,
// kadın pembe, bilinmeyen nötr mor. bg = zemin, color = ikon.
const getGenderColors = (gender) => {
  const g = normalizeGender(gender);
  if (g === "male") return { bg: "#e0f2fe", color: "#2563eb" };
  if (g === "female") return { bg: "#ffe4f0", color: "#db2777" };
  return { bg: "#ede9fe", color: "#7c3aed" };
};
const genderAvatarStyle = (gender) => {
  const c = getGenderColors(gender);
  return { background: c.bg, color: c.color };
};

// Bir ses objesinin sağlayıcısını service/servicename alanından türetir (AIVoiceTab ile aynı).
const voiceToService = (voice) => {
  const raw = String(voice?.service ?? voice?.provider ?? voice?.servicename ?? "").toLowerCase();
  if (raw.includes("eleven")) return "elevenlabs";
  if (raw.includes("google") || raw.includes("gemini")) return "google";
  if (raw.includes("azure") || raw.includes("microsoft")) return "azure";
  return "";
};

const CUSTOM_STYLE_ID = "__custom__";
const COURSE_PROMPT_ID = "__course__";
const AUTO_PROVIDER = "gemini-pro-nano";

// aideaEngine → backend scene-builder service. The value equals aideaEngine sent to the API.
// Supported engines: 1 (standard), 2 (rich), 3 (visual-first), 5 (focused), 6 (editable text), 9 (text-dense). Engine 4 removed.
// 7/8 are the character engines and are NOT always available — see CHARACTER_ENGINES below.
// The field is always sent; the legacy /generate path (no aideaEngine) is no longer used here.
export const ENGINE_OPTIONS = [
  {
    value: 1,
    labelKey: "InfographicEngine.engine1_label",
    descKey: "InfographicEngine.engine1_description",
    labelFallback: "Aidea Engine 1",
    descFallback: "Birbiriyle uyumlu, standart bir stilde infografik görseller üretir.",
  },
  {
    value: 2,
    labelKey: "InfographicEngine.engine2_label",
    descKey: "InfographicEngine.engine2_description",
    labelFallback: "Aidea Engine 2",
    descFallback:
      "Slaytlar arasında değişken ve dinamik stillerle görsel açıdan zengin infografikler üretir; sahne başına daha fazla görsel oluşturur.",
  },
  {
    value: 3,
    labelKey: "InfographicEngine.engine3_label",
    descKey: "InfographicEngine.engine3_description",
    labelFallback: "Aidea Engine 3",
    descFallback:
      "Görsellerde minimum yazı kullanan, içeriği büyük ölçüde görsellerle anlatan sade ve net bir stil üretir.",
  },
  {
    value: 5,
    labelKey: "InfographicEngine.engine5_label",
    descKey: "InfographicEngine.engine5_description",
    labelFallback: "Aidea Engine 5",
    descFallback:
      "Az sayıda, iri ve iyi yerleştirilmiş öğelerle sade ve odaklı bir stil üretir — her görselde en fazla 6 nesne ve minimum, vurucu etiketler.",
  },
  {
    value: 6,
    labelKey: "InfographicEngine.engine6_label",
    descKey: "InfographicEngine.engine6_description",
    labelFallback: "Aidea Engine 6 (Düzenlenebilir metin)",
    descFallback:
      "Görseldeki her başlık ve satır, ikonuyla birlikte ayrı düzenlenebilir bir metin kutusu olur ve anlatımda söylendiği anda hareketle ekrana gelir.",
  },
  {
    value: 7,
    labelKey: "InfographicEngine.engine7_label",
    descKey: "InfographicEngine.engine7_description",
    labelFallback: "Aidea Engine 7 (Karakterli)",
    descFallback:
      "Eğitimin sabit karakter kadrosunu kullanır — yeni görsellerde de aynı karakterler aynı yüz, saç ve kıyafetle görünür.",
  },
  {
    value: 8,
    labelKey: "InfographicEngine.engine8_label",
    descKey: "InfographicEngine.engine8_description",
    labelFallback: "Aidea Engine 8 (Karakterli, yazısız)",
    descFallback:
      "Engine 7 ile aynı karakter kadrosunu kullanır, görsellerde neredeyse hiç yazı kullanmaz.",
  },
  {
    value: 9,
    labelKey: "InfographicEngine.engine9_label",
    descKey: "InfographicEngine.engine9_description",
    labelFallback: "Aidea Engine 9 (Metin yoğun)",
    descFallback:
      "Duyulan içeriği ekranda da okutur: başlık ve maddeler anlatımın kendi kelimeleriyle, görselin üzerinde ait oldukları yere (parçanın yanına, adımın düğümüne, haritadaki yerine) yerleştirilir.",
  },
];
const DEFAULT_ENGINE = 1;

// The cast is produced ONCE, during course generation, and is never minted on
// demand. A course that was not generated with engine 7/8 therefore has no
// characters to draw, so these options are hidden for it rather than offered and
// rejected by the backend with a 400.
const CHARACTER_ENGINES = [7, 8];
// Referans görsel üst sınırı. Kurs üretiminde verilen referans görseller (en çok
// 10) modal açılınca listeye önceden gelir; o kurs için sınır onların sayısına
// genişler, yoksa kurs görsellerinin bir kısmı hiç gösterilemezdi.
const MAX_REFERENCE_VISUALS = 3;
// Only the Gemini providers accept reference images; the others take a prompt
// string alone and would drop the cast silently. Mirrors the middleware's
// GeminiDirectImageService.isGeminiProvider.
const GEMINI_PROVIDERS = ["gemini-nano", "gemini-pro-nano", "gemini"];

const LANGUAGE_OPTIONS = {
  google: [
    { value: "ar-SA", label: "Arabic" },
    { value: "de-DE", label: "German" },
    { value: "en-US", label: "English (US)" },
    { value: "es-ES", label: "Spanish" },
    { value: "fr-FR", label: "French" },
    { value: "ja-JP", label: "Japanese" },
    { value: "tr-TR", label: "Turkish" },
    { value: "az-AZ", label: "Azerbaijani" },
  ],
  elevenlabs: [{ value: "multilingual", label: "Multilingual" }],
  azure: [
    { value: "de-DE", label: "German" },
    { value: "en-GB", label: "English (UK)" },
    { value: "en-US", label: "English (US)" },
    { value: "es-ES", label: "Spanish" },
    { value: "fr-FR", label: "French" },
    { value: "ja-JP", label: "Japanese" },
    { value: "multilingual", label: "Multilingual" },
    { value: "pt-BR", label: "Portuguese (BR)" },
    { value: "tr-TR", label: "Turkish" },
    { value: "az-AZ", label: "Azerbaijani" },
  ],
};

const InfographicSceneModal = ({
  show,
  onClose,
  onCancel,
  trainingId,
  navId,
  sceneIndex,
  onSceneGenerated,
  onImageGenerated,
  onImagesFromSoundGenerated,
  onMotionSceneGenerated,
  voiceProfile,
  store,
  navItemSelected,
  navItemsIds,
  currentViewOrContained,
}) => {
  const getSoundList = () => {
    const sl = store?.getState()?.undoGroup?.present?.soundList;
    return sl?.[currentViewOrContained] || sl?.[navItemSelected] || [];
  };
  /**
   * Image URLs already on this scene. The motionize tab sends them to the
   * model as subject/mood reference — they are never reproduced, they tell it
   * what the scene is about. Read straight from the store so the tab works
   * whether the images were generated a minute ago or a month ago.
   */
  const getSceneImageUrls = () => {
    const present = store?.getState()?.undoGroup?.present;
    const sceneId = currentViewOrContained || navItemSelected;
    if (!present || !sceneId) return [];
    const urls = [];
    Object.keys(present.boxesById || {}).forEach((id) => {
      const box = present.boxesById[id];
      if (!box || box.parent !== sceneId) return;
      // pluginToolbarsById is authoritative for plugin content (boxesById.state
      // can lag), so read the toolbar and fall back to the box.
      const st = present.pluginToolbarsById?.[id]?.state || box.state || {};
      const collect = (v) => {
        if (
          typeof v === "string" &&
          /^https?:\/\//.test(v) &&
          /\.(png|jpe?g|webp)(\?|$)/i.test(v)
        ) {
          if (urls.indexOf(v) === -1) urls.push(v);
        }
      };
      collect(st.url);
      collect(st.imageUrl);
      (st.images || []).forEach((im) => collect(im?.url || im?.imageUrl || im));
    });
    return urls.slice(0, 4);
  };

  // Motionize state — the fourth tab. Mirrors the sound tab: same inputs,
  // different output (one animated scene document instead of images).
  const [motionLoading, setMotionLoading] = useState(false);
  const [motionError, setMotionError] = useState(null);
  const [motionPendingApproval, setMotionPendingApproval] = useState(null);
  const { html: motionPreviewHtml, directSrc: motionPreviewSrc } = useHtmlByUrl(
    motionPendingApproval?.result?.htmlUrl || ""
  );
  // null = Otomatik (the backend's default). A value = the user picked one.
  const [motionModel, setMotionModel] = useState(null);

  const [activeTab, setActiveTab] = useState("scene");

  // Lightbox for image preview
  const [lightboxUrl, setLightboxUrl] = useState(null);

  // Görsel Üret state
  const [imageText, setImageText] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  // Görsel Üret (generate-image) artık async job — bitince otomatik eklemeyiz,
  // "Onayla" ile mevcut sahneye eklenir (widget'tan da onaylanır). { result } tutar.
  const [imagePendingApproval, setImagePendingApproval] = useState(null);

  // Sesten Görsel Üret state
  const [soundImagesLoading, setSoundImagesLoading] = useState(false);
  const [soundImagesError, setSoundImagesError] = useState(null);
  const [sceneAudio, setSceneAudio] = useState(null);
  const [soundNarrationText, setSoundNarrationText] = useState("");
  // Üretim bitince otomatik eklemiyoruz — kullanıcı "Onayla" deyince ekliyoruz.
  // { result } tutar; null iken onay UI'ı gizli. (generate-images her zaman
  // mevcut sahneye ekler; yeni sahne "Sahne Üret" tab'ının ayrı API'si ile.)
  const [soundPendingApproval, setSoundPendingApproval] = useState(null);
  const soundAudioUrl = sceneAudio?.filePath || sceneAudio?.file || sceneAudio?.url || "";
  const soundDuration = sceneAudio?.duration || sceneAudio?.endTime || 0;
  const soundVoiceName = sceneAudio?.voiceName || sceneAudio?.voiceData?.voiceName || "";
  const soundProvider = sceneAudio?.service || sceneAudio?.voiceData?.provider || "";
  const soundLanguage = sceneAudio?.language || sceneAudio?.voiceData?.language || "";

  const handleGenerateFromSound = async () => {
    if (!sceneAudio && !soundNarrationText) return;
    setSoundImagesLoading(true);
    setSoundImagesError(null);
    try {
      const formData = new FormData();
      formData.append("narrationText", soundNarrationText);
      formData.append("audioDuration", String(soundDuration));
      if (soundAudioUrl) formData.append("audioUrl", soundAudioUrl);
      formData.append("navId", navId);
      if (trainingId) formData.append("trainingId", String(trainingId));
      // Job widget socket room key — the editor ?id= param, same value
      // useSessionImageJobs / the Activity widget join with. Backend routes it
      // to idealStudioTrainingId so session_job_update / completion reach the room.
      try {
        const roomId = new URLSearchParams(window.location.search).get("id");
        if (roomId) formData.append("idealStudioTrainingId", roomId);
      } catch {
        /* no ?id= in standalone mode — widget simply won't scope to a room */
      }
      // generate-images = mevcut sahneye ekler → sceneIndex = n (1-based).
      // Prefer the prop computed in EditorModals; fall back to store.
      let baseSceneNo = typeof sceneIndex === "number" && sceneIndex > 0 ? sceneIndex : null;
      if (baseSceneNo == null) {
        const navIds = navItemsIds || store?.getState()?.undoGroup?.present?.navItemsIds;
        if (Array.isArray(navIds)) {
          const idx = navIds.indexOf(navItemSelected ?? navId);
          if (idx >= 0) baseSceneNo = idx + 1;
        }
      }
      if (baseSceneNo != null) {
        formData.append("sceneIndex", String(baseSceneNo));
      }
      formData.append(
        "imageProvider",
        isAutoProvider ? AUTO_PROVIDER : selectedProviders[0] || AUTO_PROVIDER
      );
      const soundImgStyleId =
        !isCustomStyle && !isCoursePrompt && selectedStyle !== null ? selectedStyle : null;
      if (soundImgStyleId !== null) formData.append("imageStyleId", String(soundImgStyleId));
      const soundCustomPrompt = isCustomStyle
        ? customStyleDesc.trim()
        : isCoursePrompt
          ? coursePrompt
          : null;
      if (soundCustomPrompt) formData.append("customImagePrompt", soundCustomPrompt);
      if (selectedOrgStyleId) formData.append("organizationStyleId", selectedOrgStyleId);
      formData.append("aideaEngine", String(selectedEngine));
      selectedVisuals.forEach((file) => formData.append("visuals", file));
      if (visualTags.some((t) => t?.trim())) {
        formData.append("visualTags", JSON.stringify(visualTags));
      }

      const response = await fetch(
        `${process.env.MW_API_URL}api/infographic-scene/generate-images`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken()}` },
          body: formData,
        }
      );
      if (!response.ok) {
        const errJson = await response.json().catch(() => null);
        const errMsg = errJson?.message || errJson?.error || `HTTP ${response.status}`;
        if (isCreditRefusal(errMsg, errJson)) {
          notifyInsufficientBalance({ ...(errJson || {}), message: errJson?.message || errMsg });
          onClose?.();
          return;
        }
        throw new Error(errMsg);
      }
      // Backend artık async: 202 { jobId, room } döner, görseller arka planda
      // üretilir. jobId'yi al, iş tamamlanana kadar poll et. Otomatik EKLEMEYİZ —
      // sonucu onay bekletmeye alırız; kullanıcı "Onayla" deyince sahneye eklenir
      // (widget'tan onaylanırsa da eklenir; aynı iş iki yerden onaylanabilir).
      const { jobId } = await response.json();
      if (!jobId) throw new Error(i18n.t("Error") || "Job ID missing");
      const result = await waitForJob(jobId);
      // Modal bu sırada kapatıldıysa sonucu gösterme — widget'ta zaten görünür.
      if (!showRef.current) return;
      setSoundPendingApproval({ result, jobId });
    } catch (err) {
      setSoundImagesError(
        err.message || i18n.t("QuestionGenerator.ErrorOccurred") || "Bir hata oluştu"
      );
    } finally {
      setSoundImagesLoading(false);
    }
  };

  // Onayla → sonucu mevcut sahneye uygula, sonra kapat.
  const handleApproveSoundImages = () => {
    if (!soundPendingApproval) return;
    onImagesFromSoundGenerated?.(soundPendingApproval.result);
    // Sahneye eklendi → widget'taki "Onay Bekliyor" kartını da kapat (ve
    // widget'tan ikinci kez onaylanıp çift eklenmesini engelle).
    approveJob(soundPendingApproval.jobId);
    setSoundPendingApproval(null);
    setSelectedVisuals([]);
    setVisualTags([]);
    onClose?.();
  };

  // Vazgeç → üretilen sonucu at, onay UI'ını kapat (modal açık kalır).
  const handleDiscardSoundImages = () => {
    setSoundPendingApproval(null);
  };

  /**
   * Motionize this scene: the existing narration (and its measured word
   * timings) plus the scene's images become one procedurally animated scene.
   * Async like its siblings — three possible model turns, each followed by a
   * headless-Chrome render check, is far past any request timeout.
   */
  const handleMotionize = async () => {
    if (!sceneAudio && !soundNarrationText) return;
    setMotionLoading(true);
    setMotionError(null);
    try {
      const formData = new FormData();
      formData.append("narrationText", soundNarrationText);
      formData.append("audioDuration", String(soundDuration));
      if (soundAudioUrl) formData.append("audioUrl", soundAudioUrl);
      formData.append("navId", navId);
      if (trainingId) formData.append("trainingId", String(trainingId));
      try {
        const roomId = new URLSearchParams(window.location.search).get("id");
        if (roomId) formData.append("idealStudioTrainingId", roomId);
      } catch {
        /* no ?id= in standalone mode — widget simply won't scope to a room */
      }
      const sceneImages = getSceneImageUrls();
      if (sceneImages.length) {
        formData.append("referenceImageUrls", JSON.stringify(sceneImages));
      }
      const motionStyleId =
        !isCustomStyle && !isCoursePrompt && selectedStyle !== null ? selectedStyle : null;
      if (motionStyleId !== null) formData.append("imageStyleId", String(motionStyleId));
      const motionCustomPrompt = isCustomStyle
        ? customStyleDesc.trim()
        : isCoursePrompt
          ? coursePrompt
          : null;
      if (motionCustomPrompt) formData.append("customImagePrompt", motionCustomPrompt);
      if (selectedOrgStyleId) formData.append("organizationStyleId", selectedOrgStyleId);
      // Omitted on Otomatik so the backend keeps its own default rather than
      // having one pinned here that drifts from the catalog.
      if (motionModel) formData.append("llmModel", motionModel);

      const response = await fetch(`${process.env.MW_API_URL}api/infographic-scene/motionize`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => null);
        const errMsg = errJson?.message || errJson?.error || `HTTP ${response.status}`;
        if (isCreditRefusal(errMsg, errJson)) {
          notifyInsufficientBalance({ ...(errJson || {}), message: errJson?.message || errMsg });
          onClose?.();
          return;
        }
        throw new Error(errMsg);
      }
      const { jobId } = await response.json();
      if (!jobId) throw new Error(i18n.t("Error") || "Job ID missing");
      const result = await waitForJob(jobId);
      // Modal closed meanwhile → the widget already shows it; don't double up.
      if (!showRef.current) return;
      setMotionPendingApproval({ result, jobId });
    } catch (err) {
      setMotionError(err?.message || String(err));
    } finally {
      setMotionLoading(false);
    }
  };

  const handleApproveMotion = () => {
    if (!motionPendingApproval) return;
    onMotionSceneGenerated?.(motionPendingApproval.result);
    approveJob(motionPendingApproval.jobId);
    setMotionPendingApproval(null);
    onClose?.();
  };

  const handleDiscardMotion = () => {
    setMotionPendingApproval(null);
  };

  const handleGenerateImage = async () => {
    if (!imageText.trim()) return;
    setImageLoading(true);
    setImageError(null);
    try {
      const formData = new FormData();
      formData.append("text", imageText.trim());
      // Job widget socket room key — diğer sekmelerle aynı (?id= editör param).
      try {
        const roomId = new URLSearchParams(window.location.search).get("id");
        if (roomId) formData.append("idealStudioTrainingId", roomId);
      } catch {
        /* no ?id= in standalone mode */
      }
      // navId + sceneIndex — generate-images ile aynı. Job satırının doğru
      // sahneye bağlanması ve widget'ın sonucu doğru sahneye bırakması için.
      if (navId) formData.append("navId", navId);
      let imgBaseSceneNo = typeof sceneIndex === "number" && sceneIndex > 0 ? sceneIndex : null;
      if (imgBaseSceneNo == null) {
        const navIds = navItemsIds || store?.getState()?.undoGroup?.present?.navItemsIds;
        if (Array.isArray(navIds)) {
          const idx = navIds.indexOf(navItemSelected ?? navId);
          if (idx >= 0) imgBaseSceneNo = idx + 1;
        }
      }
      if (imgBaseSceneNo != null) {
        formData.append("sceneIndex", String(imgBaseSceneNo));
      }
      const imgCustomPrompt = isCustomStyle
        ? customStyleDesc.trim()
        : isCoursePrompt
          ? coursePrompt
          : null;
      if (imgCustomPrompt) formData.append("customImagePrompt", imgCustomPrompt);
      formData.append(
        "provider",
        isAutoProvider ? AUTO_PROVIDER : selectedProviders[0] || AUTO_PROVIDER
      );
      formData.append("aideaEngine", String(selectedEngine));
      const imgStyleId =
        !isCustomStyle && !isCoursePrompt && selectedStyle !== null ? selectedStyle : null;
      if (imgStyleId !== null) formData.append("imageStyleId", String(imgStyleId));
      if (trainingId) formData.append("trainingId", String(trainingId));
      if (selectedOrgStyleId) formData.append("organizationStyleId", selectedOrgStyleId);
      selectedVisuals.forEach((file) => formData.append("visuals", file));
      if (visualTags.some((t) => t?.trim())) {
        formData.append("visualTags", JSON.stringify(visualTags));
      }

      const response = await fetch(
        `${process.env.MW_API_URL}api/infographic-scene/generate-image`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken()}` },
          body: formData,
        }
      );
      if (!response.ok) {
        const errJson = await response.json().catch(() => null);
        const errMsg = errJson?.message || errJson?.error || `HTTP ${response.status}`;
        if (isCreditRefusal(errMsg, errJson)) {
          notifyInsufficientBalance({ ...(errJson || {}), message: errJson?.message || errMsg });
          onClose?.();
          return;
        }
        throw new Error(errMsg);
      }
      // Backend artık async: 202 { jobId } döner. Poll et, otomatik EKLEMEYİZ —
      // onay bekletiriz; "Onayla" deyince görsel mevcut sahneye eklenir.
      const { jobId } = await response.json();
      if (!jobId) throw new Error(i18n.t("Error") || "Job ID missing");
      const result = await waitForJob(jobId);
      // Modal bu sırada kapatıldıysa sonucu gösterme — widget'ta zaten görünür.
      if (!showRef.current) return;
      setImagePendingApproval({ result, jobId });
    } catch (err) {
      setImageError(err.message || i18n.t("QuestionGenerator.ErrorOccurred") || "Bir hata oluştu");
    } finally {
      setImageLoading(false);
    }
  };

  // Görsel job sonucundan tekil URL çıkar: önce imageUrl, yoksa ilk filesUploaded.
  const extractImageUrl = (result) => {
    if (!result) return null;
    if (result.imageUrl) return result.imageUrl;
    const first = Object.values(result.filesUploaded || {})
      .map((f) => f?.url)
      .filter(Boolean);
    return first[0] || null;
  };

  // Onayla → görseli mevcut sahneye ekle, sonra kapat.
  const handleApproveImage = () => {
    if (!imagePendingApproval) return;
    const url = extractImageUrl(imagePendingApproval.result);
    onImageGenerated?.(url);
    approveJob(imagePendingApproval.jobId);
    setImagePendingApproval(null);
    setImageText("");
    setSelectedVisuals([]);
    setVisualTags([]);
    onClose?.();
  };

  const handleDiscardImage = () => {
    setImagePendingApproval(null);
  };

  const [narrationText, setNarrationText] = useState("");
  const [fetchingSceneText, setFetchingSceneText] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Sahne Üret (generate) artık async job — bitince otomatik eklemeyiz, "Onayla"
  // ile yeni sahne olarak eklenir (widget'tan da onaylanır). { result } tutar.
  const [scenePendingApproval, setScenePendingApproval] = useState(null);
  // İş tepsiden onaylanır/reddedilirse (tepsi onayı sahneyi zaten ekler) açık
  // modalın "Onayla"sı bayatlar ve ikinci kez eklerdi → o işin bekleyen
  // sonucunu düşür. Dört sekme de { result, jobId } tutuyor.
  useEffect(
    () =>
      onJobsSettled((jobIds) => {
        const drop = (prev) => (prev && jobIds.includes(prev.jobId) ? null : prev);
        setScenePendingApproval(drop);
        setSoundPendingApproval(drop);
        setImagePendingApproval(drop);
        setMotionPendingApproval(drop);
      }),
    []
  );

  // Voice — AIVoiceTab ile aynı pattern: profil sadece display, state boş başlar
  const profileService = SERVICE_NORMALIZE_MAP[voiceProfile?.voiceServiceProvider] || "";
  const profileLanguage = voiceProfile?.voiceLanguage || "";
  // profileVoiceName = GÖSTERİM adı (voiceDetail.name, ör. "Ahmet (Turkish Male)").
  // profileVoiceId  = backend TTS'in beklediği HAM provider ses id'si
  // (voiceProfile.voiceName, ör. "tr-TR-AhmetNeural" = tts_voices.providerId).
  // Payload'a HER ZAMAN id gitmeli (gösterim adı değil) — aksi halde backend sesi
  // çözemez ve narration üretmeden sahne oluşur (AIVoiceTab:1757 aynı ayrımı yapar).
  const profileVoiceName = voiceProfile?.voiceDetail?.name || voiceProfile?.voiceName || "";
  const profileVoiceId = voiceProfile?.voiceName || "";
  const profileGender = normalizeGender(
    voiceProfile?.voiceDetail?.gender || voiceProfile?.voiceGender || ""
  );

  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedService, setSelectedService] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [samplePlayingId, setSamplePlayingId] = useState(null);
  const sampleAudioRef = useRef(null);
  const [selectedVisuals, setSelectedVisuals] = useState([]);
  const [visualTags, setVisualTags] = useState([]);
  // Kursun kendi referans görsellerinin sayısı (modal açılınca yüklenir).
  const [courseVisualCount, setCourseVisualCount] = useState(0);
  const maxVisuals = Math.max(MAX_REFERENCE_VISUALS, courseVisualCount);
  // Her açılış/kapanışta artar; geç gelen kurs görselleri eski bir açılışa aitse atılır.
  const courseVisualsLoadRef = useRef(0);
  // Kurs görselleri inerken yükleme kartları yerine bekleme durumu gösterilir;
  // yoksa modal önce boş açılıp birkaç saniye sonra dolu hale atlıyor.
  const [courseVisualsLoading, setCourseVisualsLoading] = useState(false);
  // Açılış efekti (useEffect) ilk çizimden SONRA çalışır; bekleme durumu orada
  // açılsaydı modal bir kare boş yükleme kartlarıyla görünürdü.
  useLayoutEffect(() => {
    if (show && trainingId) setCourseVisualsLoading(true);
  }, [show, trainingId]);
  const visualsInputRef = useRef(null);
  // Referans görsel yükleme kartında sürükle-bırak vurgusu.
  const [refDragActive, setRefDragActive] = useState(false);
  // Kütüphaneden seçim: modal içinde açılan (popup üstüne popup yok) görsel grid'i.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryFiles, setLibraryFiles] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState(null);
  const [librarySearch, setLibrarySearch] = useState("");
  // Kütüphaneden seçilen görsel indirilip File'a çevrilirken beklet (id set).
  const [libraryAddingId, setLibraryAddingId] = useState(null);
  // Modal'ın açık olup olmadığını senkron takip eder. waitForJob arka planda
  // devam ederken modal kapatılırsa, geç çözülen sonuç pending state'i tekrar
  // doldurmasın (kullanıcı işi zaten widget'tan takip/onaylar).
  const showRef = useRef(show);
  useEffect(() => {
    showRef.current = show;
  }, [show]);

  // Kredi tahmini (görsel modalı ile aynı pattern)
  const [creditInfo, setCreditInfo] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditPopoverOpen, setCreditPopoverOpen] = useState(false);
  const creditBtnRef = useRef(null);

  // Image styles
  const [styles, setStyles] = useState([]);
  const [stylesLoading, setStylesLoading] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedEngine, setSelectedEngine] = useState(DEFAULT_ENGINE);
  const [engineOpen, setEngineOpen] = useState(false);
  // Whether this training was generated with engine 7/8 and still carries its
  // cast. Only then are the character engines offered.
  const [hasCast, setHasCast] = useState(false);
  const [customStyleDesc, setCustomStyleDesc] = useState("");
  const [coursePrompt, setCoursePrompt] = useState(null);

  // Organization styles
  const [organizationStyles, setOrganizationStyles] = useState([]);
  const [orgStylesLoading, setOrgStylesLoading] = useState(false);
  const [selectedOrgStyleId, setSelectedOrgStyleId] = useState(null);

  const isCustomStyle = selectedStyle === CUSTOM_STYLE_ID;
  const isCoursePrompt = selectedStyle === COURSE_PROMPT_ID;

  // Stil bölümleri varsayılan olarak tek satır; "Tümünü Göster" ile açılır.
  const [showAllStyles, setShowAllStyles] = useState(false);
  const [showAllOrgStyles, setShowAllOrgStyles] = useState(false);

  // Image provider
  const [isAutoProvider, setIsAutoProvider] = useState(true);
  const [selectedProviders, setSelectedProviders] = useState([AUTO_PROVIDER]);

  const selectAutoMode = () => {
    if (isAutoProvider) return;
    setIsAutoProvider(true);
    setSelectedProviders([AUTO_PROVIDER]);
  };

  const selectCustomMode = () => {
    if (!isAutoProvider) return;
    setIsAutoProvider(false);
  };

  const handleVisualsChange = (e) => {
    const files = Array.from(e.target.files || []);
    // 500 MB üst sınırı: aşan dosyaları filtrele; aşan varsa uyar.
    const accepted = files.filter((f) => !isFileTooLarge(f));
    if (accepted.length < files.length) setError(fileTooLargeMessage());
    setSelectedVisuals((prev) => [...prev, ...accepted].slice(0, maxVisuals));
    e.target.value = "";
  };

  // Sürükle-bırak ile eklenen dosyalar — sadece görselleri kabul et.
  const handleVisualsDrop = (files) => {
    const imgs = Array.from(files || []).filter((f) => (f.type || "").startsWith("image/"));
    if (imgs.length === 0) return;
    // 500 MB üst sınırı: aşan dosyaları filtrele; aşan varsa uyar.
    const accepted = imgs.filter((f) => !isFileTooLarge(f));
    if (accepted.length < imgs.length) setError(fileTooLargeMessage());
    if (accepted.length === 0) return;
    setSelectedVisuals((prev) => [...prev, ...accepted].slice(0, maxVisuals));
  };

  // Kütüphaneden (editörün dosya kütüphanesi) sadece görselleri getirir.
  const IMAGE_FILE_TYPES = [
    "image",
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/svg+xml",
    "image/webp",
  ];

  const loadLibraryImages = useCallback(async (search) => {
    setLibraryLoading(true);
    setLibraryError(null);
    try {
      const res = await fetchIdealStudioFiles({
        fileType: IMAGE_FILE_TYPES,
        limit: 60,
        offset: 0,
        search: search || undefined,
      });
      const files = res && Array.isArray(res.files) ? res.files : [];
      setLibraryFiles(
        files
          .map((f) => ({
            id: f.id,
            name: f.fileName || f.filename || "image",
            url: f.fileVirtualPath,
          }))
          .filter((f) => f.url)
      );
    } catch {
      setLibraryError(i18n.t("files_load_error") || "Dosyalar yüklenemedi");
      setLibraryFiles([]);
    } finally {
      setLibraryLoading(false);
    }
  }, []);

  const openLibrary = () => {
    setLibraryOpen(true);
    setLibrarySearch("");
    loadLibraryImages("");
  };

  // Kütüphaneden bir görsel seçilince: URL'yi indirip File'a çevir, referans
  // görseller listesine ekle (backend `visuals` alanını dosya olarak bekliyor).
  const handlePickFromLibrary = async (file) => {
    if (!file?.url || selectedVisuals.length >= maxVisuals || libraryAddingId) return;
    setLibraryAddingId(file.id);
    try {
      const resp = await fetch(file.url, credentialsFor(file.url));
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const ext = (file.url.split("?")[0].split(".").pop() || "png").toLowerCase();
      const safeName = /\.[a-z0-9]+$/i.test(file.name) ? file.name : `${file.name}.${ext}`;
      const asFile = new File([blob], safeName, { type: blob.type || "image/png" });
      setSelectedVisuals((prev) => [...prev, asFile].slice(0, maxVisuals));
      setLibraryOpen(false);
    } catch {
      setLibraryError(i18n.t("files_load_error") || "Görsel yüklenemedi");
    } finally {
      setLibraryAddingId(null);
    }
  };

  const setVisualTag = (index, value) => {
    setVisualTags((prev) => {
      const next = [...prev];
      next[index] = value.slice(0, 60);
      return next;
    });
  };

  // Kursun referans görsellerini getirip listenin başına ekler. `loadSeq` artık
  // güncel açılış değilse (modal kapandı/yeniden açıldı) sonuç atılır.
  const loadCourseReferenceVisuals = async (loadSeq) => {
    try {
      const resp = await fetch(
        `${process.env.MW_API_URL}api/infographic-scene/reference-visuals/${trainingId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      const visuals = Array.isArray(data?.visuals) ? data.visuals : [];
      if (!visuals.length) return;

      const loaded = await Promise.all(
        visuals.map(async ({ url, tag }) => {
          try {
            const r = await fetch(url, credentialsFor(url));
            if (!r.ok) return null;
            const blob = await r.blob();
            const name = url.split("?")[0].split("/").pop() || "reference.png";
            return {
              file: new File([blob], name, { type: blob.type || "image/png" }),
              tag: tag || "",
            };
          } catch {
            return null;
          }
        })
      );
      const items = loaded.filter(Boolean);
      if (!items.length || courseVisualsLoadRef.current !== loadSeq) return;

      const cap = Math.max(MAX_REFERENCE_VISUALS, items.length);
      setCourseVisualCount(items.length);
      // Görsel ve etiket dizileri aynı indekste hizalı; ikisine de aynı sayıda
      // öğe başa eklenince hizalama korunur.
      setSelectedVisuals((prev) => [...items.map((i) => i.file), ...prev].slice(0, cap));
      setVisualTags((prev) => [...items.map((i) => i.tag), ...prev].slice(0, cap));
    } catch {
      // Referans görseller yüklenemezse modal boş listeyle çalışmaya devam eder.
    } finally {
      if (courseVisualsLoadRef.current === loadSeq) setCourseVisualsLoading(false);
    }
  };

  const removeVisual = (index) => {
    setSelectedVisuals((prev) => prev.filter((_, i) => i !== index));
    setVisualTags((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleProvider = (value) => {
    setSelectedProviders([value]);
  };

  const loadVoices = useCallback(async (service, language, gender = "", page = 1) => {
    setLoadingVoices(true);
    // service boş ("Tümü") → service paramı eklenmez, API tüm sağlayıcıların
    // seslerini döner (AIVoiceTab ile aynı davranış).
    const serviceName = API_SERVICE_MAP[service] || service;
    try {
      const params = new URLSearchParams();
      if (serviceName) params.append("service", serviceName);
      if (language) params.append("language", language);
      if (gender) params.append("gender", gender);
      params.append("page", page.toString());
      params.append("pageSize", "12");

      const response = await fetch(
        `${process.env.MW_API_URL}api/course/voices?${params.toString()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const list = Array.isArray(data.voices) ? data.voices : [];
          setVoices(list);
          // Toplam sayfa: API gerçek toplamı pagination.totalPages/total ile döner —
          // AIVoiceTab ile aynı sıra. Zayıf fallback (page+1) yalnızca hiçbiri yoksa
          // kullanılır; aksi halde sayfalar tek seferde 1..N gelir.
          if (data.pagination?.totalPages) setTotalPages(data.pagination.totalPages);
          else if (data.pagination?.total) setTotalPages(Math.ceil(data.pagination.total / 12));
          else if (data.totalPages) setTotalPages(data.totalPages);
          else if (data.totalCount) setTotalPages(Math.ceil(data.totalCount / 12));
          else setTotalPages(list.length >= 12 ? page + 1 : page);
          return list;
        }
      }
    } catch {
      setVoices([]);
    } finally {
      setLoadingVoices(false);
    }
    return [];
  }, []);

  const loadOrganizationStyles = useCallback(async () => {
    setOrgStylesLoading(true);
    try {
      const response = await fetch(`${process.env.MW_API_URL}api/organization-image-styles`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      const items = (data?.styles || []).filter((s) => s.isActive);
      setOrganizationStyles(items);
    } catch {
      setOrganizationStyles([]);
    } finally {
      setOrgStylesLoading(false);
    }
  }, []);

  // engine verilirse backend yalnızca o aideaEngine'e atanmış sahne stillerini
  // döner (katı filtre). Engine değişince yeniden çağrılır.
  const loadStyles = useCallback(async (engine) => {
    setStylesLoading(true);
    try {
      const data = await ImagePromptLibraryService.getImagePromptLibrary({
        limit: 200,
        offset: 0,
        orderBy: "createdAt",
        orderDirection: "DESC",
        isSceneStyle: true,
        engine,
      });
      const items = data?.libraries || data?.items || data || [];
      setStyles(items);
      if (items.length > 0) {
        const saved = localStorage.getItem("imagePromptLibraryId");
        const savedNum = saved ? parseInt(saved, 10) : null;
        const match = savedNum ? items.find((i) => i.id === savedNum) : null;
        const fallback = match ? savedNum : items[0].id;
        // Engine değişince eski seçim filtre dışı kalabilir: geçerliyse koru,
        // değilse ilk uygun stile düş. Özel/kurs kartlarına dokunma.
        setSelectedStyle((prev) => {
          if (prev === CUSTOM_STYLE_ID || prev === COURSE_PROMPT_ID) return prev;
          if (prev !== null && items.some((i) => i.id === prev)) return prev;
          return fallback;
        });
      }
    } catch {
      setStyles([]);
    } finally {
      setStylesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!show) {
      courseVisualsLoadRef.current += 1;
      setSelectedVisuals([]);
      setVisualTags([]);
      setCourseVisualCount(0);
      setCourseVisualsLoading(false);
      setRefDragActive(false);
      setLibraryOpen(false);
      setLibraryFiles([]);
      setLibraryError(null);
      setLibrarySearch("");
      // Modal kapanınca üretim/onay artıklarını temizle — tekrar açılınca eski
      // sonuç/spinner görünmesin. Devam eden iş widget'tan takip edilir.
      setScenePendingApproval(null);
      setSoundPendingApproval(null);
      setImagePendingApproval(null);
      setMotionPendingApproval(null);
      setMotionError(null);
      setError(null);
      setSoundImagesError(null);
      setImageError(null);
      return;
    }
    setSelectedOrgStyleId(null);
    // Stiller ayrı bir efektte (selectedEngine bağımlı) yüklenir — bkz. aşağıda.
    loadOrganizationStyles();

    // Karakter kadrosu: yalnızca engine 7/8 ile üretilmiş eğitimlerde var.
    // Kadro kurs üretiminde BİR KEZ oluşturulur, sonradan üretilmez — yoksa
    // karakterli motorlar listede hiç görünmez.
    setHasCast(false);
    if (trainingId) {
      fetch(`${process.env.MW_API_URL}api/infographic-scene/cast/${trainingId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => setHasCast(Boolean(data?.hasCast)))
        .catch(() => setHasCast(false));
    }

    // Kurs üretilirken verilen referans görseller ("Görsellerimi referans olarak
    // kullan") listeye önceden gelir; yazar sahne için silebilir ya da yenilerini
    // ekleyebilir. Backend `visuals`'ı dosya olarak beklediği için kütüphaneden
    // seçimdeki gibi indirilip File'a çevrilir ve öyle gönderilir.
    const loadSeq = ++courseVisualsLoadRef.current;
    if (trainingId) {
      setCourseVisualsLoading(true);
      loadCourseReferenceVisuals(loadSeq);
    }

    // Eğitimde kullanılan prompt'u yükle
    if (trainingId) {
      fetch(`${process.env.MW_API_URL}api/ideal-studio/training/${trainingId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          const prompt = data?.customImagePrompt || data?.training?.customImagePrompt;
          const styleId = data?.imagePromptLibraryId || data?.training?.imagePromptLibraryId;
          const orgStyleId =
            data?.organizationImageStyleId || data?.training?.organizationImageStyleId;
          if (orgStyleId) {
            setSelectedOrgStyleId(orgStyleId);
            setSelectedStyle(null);
          } else if (prompt) {
            setCoursePrompt(prompt);
            setSelectedStyle(COURSE_PROMPT_ID);
          } else if (styleId) {
            setSelectedStyle(styleId);
          }
        })
        .catch(() => {});
    }

    if (profileService) {
      // AIVoiceTab defaultLang ile aynı: elevenlabs → multilingual, google → ""
      // (google'da dil filtresi uygulanmaz; profileLanguage geçilirse API boş döner),
      // diğerleri → profil dili.
      const lang =
        profileService === "elevenlabs"
          ? "multilingual"
          : profileService === "google"
            ? ""
            : profileLanguage;
      // AIVoiceTab gibi: seçili sesin sağlayıcısı/dili önceden seçili gelsin —
      // panel açıldığında provider chip aktif ve grid doğru sağlayıcıyı gösterir.
      setSelectedService(profileService);
      setSelectedLanguage(lang);
      // Kartı hemen profil adı + cinsiyetiyle göster (loadVoices ağ çağrısı
      // boyunca boş/nötr kalmasın). Cinsiyet → avatar rengi (kadın pembe, erkek mavi).
      if (profileVoiceName) {
        // id = ham provider ses id'si (payload'da kullanılır); name = gösterim adı.
        setSelectedVoice({
          id: profileVoiceId || profileVoiceName,
          name: profileVoiceName,
          gender: profileGender,
        });
        if (profileGender) setSelectedGender(profileGender);
      }
      // AIVoiceTab ile aynı: TÜM sesleri değil, yalnızca 1. sayfayı (12 ses)
      // sayfalı yükle. Seçili ses 1. sayfadaysa eşleştirilip vurgulanır; değilse
      // grid sadece 1. sayfayı gösterir (tüm liste dökülmez).
      loadVoices(profileService, lang, "", 1).then((list) => {
        if (!profileVoiceName) return;
        const search = profileVoiceName.toLowerCase().trim();
        const match = (list || []).find(
          (v) =>
            String(v.id || "")
              .toLowerCase()
              .trim() === search ||
            String(v.name || "")
              .toLowerCase()
              .trim() === search
        );
        if (match) {
          setSelectedVoice(match);
          // Seçili sesin cinsiyeti — chip ve avatar rengi doğru gelsin.
          if (match.gender) setSelectedGender(normalizeGender(match.gender));
        }
      });
    }
  }, [show]);

  // Engine bazlı stil filtreleme (backend `engine` query paramı ile aynı davranış:
  // yalnızca seçili aideaEngine'e atanmış sahne stilleri gelir). Modal açılınca ve
  // engine değişince stil listesini yeniden çeker.
  useEffect(() => {
    if (show) loadStyles(selectedEngine);
  }, [show, selectedEngine, loadStyles]);

  useEffect(() => {
    const soundList = getSoundList();
    const narrationSound =
      Array.isArray(soundList) && soundList.length > 0
        ? soundList.find((s) => s.file || s.filePath) || null
        : null;
    setSceneAudio(narrationSound || null);

    if (!narrationSound) {
      setSoundNarrationText("");
      return;
    }

    // Önce objedeki metni dene
    const inlineText =
      narrationSound.narrationTextOriginal ||
      narrationSound.narrationText ||
      narrationSound.voiceText;
    if (inlineText) {
      setSoundNarrationText(inlineText);
      return;
    }

    // Yoksa Sound modal ile aynı şekilde .txt dosyasını fetch et
    const fileUrl = narrationSound.file || narrationSound.filePath;
    if (fileUrl) {
      fetch(withSidecarSuffix(fileUrl, ".txt"), { credentials: "include" })
        .then((r) => (r.ok ? r.text() : null))
        .then((text) => {
          if (!text) return;
          try {
            const meta = JSON.parse(text);
            if (meta.VoiceText) setSoundNarrationText(meta.VoiceText);
          } catch {
            // düz metin ise doğrudan kullan
            if (text.trim()) setSoundNarrationText(text.trim());
          }
        })
        .catch(() => {});
    }
  }, [show, navItemSelected, currentViewOrContained]);

  useEffect(() => {
    return () => {
      if (sampleAudioRef.current) {
        sampleAudioRef.current.pause();
        sampleAudioRef.current = null;
      }
    };
  }, []);

  const handleServiceChange = (service) => {
    setSelectedService(service);
    setSelectedVoice(null);
    setVoices([]);
    setCurrentPage(1);
    setTotalPages(1);
    // Provider değişince cinsiyet her zaman "Tümü"ye döner.
    setSelectedGender("");
    if (service === "elevenlabs") {
      setSelectedLanguage("multilingual");
      loadVoices(service, "multilingual", "", 1);
    } else if (service) {
      // Google takes no language filter.
      const lang =
        service === "google"
          ? ""
          : pickContentLocale(
              (LANGUAGE_OPTIONS[service] || []).map((l) => l.value),
              ""
            );
      setSelectedLanguage(lang);
      setSelectedGender("");
      loadVoices(service, lang, "", 1);
    } else {
      // "Tümü" (All) — tüm sağlayıcıların seslerini yükle (service paramı yok).
      setSelectedLanguage("");
      setSelectedGender("");
      loadVoices("", "", "", 1);
    }
  };

  const handleSamplePlay = useCallback(
    (voice) => {
      let sampleUrl = null;
      if (voice.sampleUrlsByLanguage) {
        const langs = Object.keys(voice.sampleUrlsByLanguage);
        // Örnek dil önceliği AIVoiceTab ile aynı: açık olan sayfa/editör dili >
        // TR > EN > seçili üretim dili > ilk mevcut dil.
        const lang = pickSampleLanguage(langs, { selectedLanguage });
        sampleUrl = lang && voice.sampleUrlsByLanguage[lang]?.v1;
      }
      if (!sampleUrl) sampleUrl = voice.sampleUrl || voice.audioSampleUrl || voice.previewUrl;
      if (!sampleUrl) return;

      if (samplePlayingId === voice.id && sampleAudioRef.current) {
        sampleAudioRef.current.pause();
        sampleAudioRef.current = null;
        setSamplePlayingId(null);
        return;
      }
      if (sampleAudioRef.current) sampleAudioRef.current.pause();
      const audio = new Audio(sampleUrl);
      sampleAudioRef.current = audio;
      setSamplePlayingId(voice.id);
      audio.onended = () => {
        setSamplePlayingId(null);
        sampleAudioRef.current = null;
      };
      audio.play().catch(() => {
        setSamplePlayingId(null);
        sampleAudioRef.current = null;
      });
    },
    [samplePlayingId, selectedLanguage]
  );

  const resolveStyleLabel = (style, index) => {
    if (style?.translationKey) {
      const t = i18n.t(style.translationKey);
      if (t && t !== style.translationKey) return t;
    }
    return (
      style?.localizedName || style?.label || style?.name || style?.promptText || `Style ${index}`
    );
  };

  // Apply the agent's intent when the popup is opened by a chat tool card.
  // prompt  → narrationText (scene tab)
  // settings.styleId → selectedStyle (by numeric id, if present in loaded styles)
  // settings.style   → selectedStyle (by name fallback)
  // FIX 4: if the matched style's index in `styles` is >= 6 (visible count),
  // expand the style grid so the selected style is visible.
  const applyInfographicIntent = useCallback(
    ({ prompt, settings }) => {
      if (typeof prompt === "string" && prompt.trim()) {
        setNarrationText(prompt.trim());
        setActiveTab("scene");
      }
      const s = settings || {};
      let matchedId = null;
      if (s.styleId != null && Array.isArray(styles) && styles.some((st) => st.id === s.styleId)) {
        matchedId = s.styleId;
      } else if (s.style && Array.isArray(styles) && styles.length) {
        const match = styles.find(
          (st) => (st?.name || st?.promptText || "").toLowerCase() === String(s.style).toLowerCase()
        );
        if (match) matchedId = match.id;
      }
      if (matchedId !== null) {
        setSelectedStyle(matchedId);
        // FIX 4: expand styles grid if the matched style is beyond the first 6.
        const idx = Array.isArray(styles) ? styles.findIndex((st) => st.id === matchedId) : -1;
        if (idx >= 6) setShowAllStyles(true);
      }
    },
    [styles]
  );

  usePopupPrefill("infographicScene", applyInfographicIntent);

  const handleGenerate = async () => {
    if (!narrationText.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("narrationText", narrationText.trim());
      formData.append("navId", navId);
      if (trainingId) formData.append("trainingId", String(trainingId));
      try {
        const roomId = new URLSearchParams(window.location.search).get("id");
        if (roomId) formData.append("idealStudioTrainingId", roomId);
      } catch {
        /* no ?id= in standalone mode */
      }
      // "Sahne Üret" (Yeni Sahne Oluştur) tab → mevcut sahneden sonra yeni sahne
      // ekler → sceneIndex = n+1. n = navItemsIds.indexOf(navItemSelected)+1.
      {
        let sceneBaseNo = typeof sceneIndex === "number" && sceneIndex > 0 ? sceneIndex : null;
        if (sceneBaseNo == null) {
          const navIds = navItemsIds || store?.getState()?.undoGroup?.present?.navItemsIds;
          if (Array.isArray(navIds)) {
            const idx = navIds.indexOf(navItemSelected ?? navId);
            if (idx >= 0) sceneBaseNo = idx + 1;
          }
        }
        if (sceneBaseNo != null) {
          formData.append("sceneIndex", String(sceneBaseNo + 1));
        }
      }
      // Payload'a ham ses id'si gönder — selectedVoice.name (gösterim adı) ASLA
      // fallback olmamalı; profil fallback'i de id (profileVoiceId) olmalı.
      const voiceName = selectedVoice?.id || profileVoiceId || profileVoiceName;
      if (voiceName) formData.append("voiceName", voiceName);
      formData.append(
        "voiceServiceType",
        String(
          VOICE_SERVICE_TYPE_MAP[selectedService] ?? VOICE_SERVICE_TYPE_MAP[profileService] ?? 1
        )
      );
      const voiceLanguage = selectedLanguage || profileLanguage;
      if (voiceLanguage) formData.append("voiceLanguage", voiceLanguage);
      formData.append(
        "imageProvider",
        isAutoProvider ? AUTO_PROVIDER : selectedProviders[0] || AUTO_PROVIDER
      );
      const sceneStyleId =
        !isCustomStyle && !isCoursePrompt && selectedStyle !== null ? selectedStyle : null;
      if (sceneStyleId !== null) formData.append("imageStyleId", String(sceneStyleId));
      const sceneCustomPrompt = isCustomStyle
        ? customStyleDesc.trim()
        : isCoursePrompt
          ? coursePrompt
          : null;
      if (sceneCustomPrompt) formData.append("customImagePrompt", sceneCustomPrompt);
      if (selectedOrgStyleId) formData.append("organizationStyleId", selectedOrgStyleId);
      formData.append("aideaEngine", String(selectedEngine));
      selectedVisuals.forEach((file) => formData.append("visuals", file));
      if (visualTags.some((t) => t?.trim())) {
        formData.append("visualTags", JSON.stringify(visualTags));
      }

      const response = await fetch(`${process.env.MW_API_URL}api/infographic-scene/generate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => null);
        const errMsg = errJson?.message || errJson?.error || `HTTP ${response.status}`;
        if (isCreditRefusal(errMsg, errJson)) {
          notifyInsufficientBalance({ ...(errJson || {}), message: errJson?.message || errMsg });
          onClose?.();
          return;
        }
        throw new Error(errMsg);
      }
      // Backend artık async: 202 { jobId } döner. Poll et, otomatik EKLEMEYİZ —
      // onay bekletiriz; "Onayla" deyince yeni sahne olarak eklenir.
      const { jobId } = await response.json();
      if (!jobId) throw new Error(i18n.t("Error") || "Job ID missing");
      const result = await waitForJob(jobId);
      // Modal bu sırada kapatıldıysa sonucu gösterme — widget'ta zaten görünür.
      if (!showRef.current) return;
      setScenePendingApproval({ result, jobId });
    } catch (err) {
      setError(err.message || i18n.t("QuestionGenerator.ErrorOccurred") || "Bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Onayla → sonucu yeni sahne olarak ekle, sonra kapat.
  const handleApproveScene = () => {
    if (!scenePendingApproval) return;
    onSceneGenerated?.(scenePendingApproval.result);
    approveJob(scenePendingApproval.jobId);
    setScenePendingApproval(null);
    setNarrationText("");
    setSelectedVisuals([]);
    setVisualTags([]);
    onClose?.();
  };

  const handleDiscardScene = () => {
    setScenePendingApproval(null);
  };

  // Mevcut sahnenin kayıtlı anlatım metnini scene-audios endpoint'inden çekip
  // "Anlatım Metni" alanına doldurur.
  const handleFetchSceneText = async (setter = setNarrationText) => {
    setFetchingSceneText(true);
    try {
      const data = await SceneAudioService.getSceneAudios({});
      const scenes = Array.isArray(data?.scenes) ? data.scenes : [];
      const targetNavId = currentViewOrContained || navItemSelected;
      const scene =
        scenes.find((s) => s.navId === targetNavId) ||
        scenes.find((s) => s.navId === navItemSelected);
      const narrationAudio =
        scene?.audios?.find((a) => a.narrationText) || scene?.audios?.[0] || null;
      if (narrationAudio?.narrationText) {
        setter(narrationAudio.narrationText);
      }
    } catch (e) {
      console.warn("[InfographicSceneModal] fetch scene narration failed", e);
    } finally {
      setFetchingSceneText(false);
    }
  };

  // Aktif sekmenin generate isteğiyle aynı payload'u kurup "-credit" endpoint'ine yollar.
  // Sadece maliyeti tahmin eder; üretim yapmaz. Dönüş şekli görsel modalıyla aynı:
  // { requiredCredits, availableCredits, hasSufficientBalance, providers?: [{label, cost}] }
  const checkCredit = async () => {
    const provider = isAutoProvider ? AUTO_PROVIDER : selectedProviders[0] || AUTO_PROVIDER;
    const styleId =
      !isCustomStyle && !isCoursePrompt && selectedStyle !== null ? selectedStyle : null;
    const customPrompt = isCustomStyle
      ? customStyleDesc.trim()
      : isCoursePrompt
        ? coursePrompt
        : null;

    const formData = new FormData();
    let endpoint;
    if (isMotionTab) {
      endpoint = "motionize";
      formData.append("narrationText", soundNarrationText);
      formData.append("audioDuration", String(soundDuration));
      if (soundAudioUrl) formData.append("audioUrl", soundAudioUrl);
      formData.append("navId", navId);
    } else if (isSoundTab) {
      endpoint = "generate-images";
      formData.append("narrationText", soundNarrationText);
      formData.append("audioDuration", String(soundDuration));
      if (soundAudioUrl) formData.append("audioUrl", soundAudioUrl);
      formData.append("navId", navId);
      formData.append("imageProvider", provider);
      formData.append("aideaEngine", String(selectedEngine));
    } else if (isImageTab) {
      endpoint = "generate-image";
      formData.append("text", imageText.trim());
      formData.append("provider", provider);
      formData.append("aideaEngine", String(selectedEngine));
    } else {
      endpoint = "generate";
      formData.append("narrationText", narrationText.trim());
      formData.append("navId", navId);
      // Payload'a ham ses id'si gönder — selectedVoice.name (gösterim adı) ASLA
      // fallback olmamalı; profil fallback'i de id (profileVoiceId) olmalı.
      const voiceName = selectedVoice?.id || profileVoiceId || profileVoiceName;
      if (voiceName) formData.append("voiceName", voiceName);
      formData.append(
        "voiceServiceType",
        String(
          VOICE_SERVICE_TYPE_MAP[selectedService] ?? VOICE_SERVICE_TYPE_MAP[profileService] ?? 1
        )
      );
      const voiceLanguage = selectedLanguage || profileLanguage;
      if (voiceLanguage) formData.append("voiceLanguage", voiceLanguage);
      formData.append("imageProvider", provider);
      formData.append("aideaEngine", String(selectedEngine));
    }
    if (trainingId) formData.append("trainingId", String(trainingId));
    if (styleId !== null) formData.append("imageStyleId", String(styleId));
    if (customPrompt) formData.append("customImagePrompt", customPrompt);
    if (selectedOrgStyleId) formData.append("organizationStyleId", selectedOrgStyleId);
    selectedVisuals.forEach((file) => formData.append("visuals", file));
    if (visualTags.some((t) => t?.trim())) {
      formData.append("visualTags", JSON.stringify(visualTags));
    }

    const response = await fetch(
      `${process.env.MW_API_URL}api/infographic-scene/${endpoint}-credit`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err?.message || i18n.t("InsufficientBalance.Failed") || "Credit check failed"
      );
    }
    return response.json();
  };

  const handleCreditCheck = async () => {
    if (creditPopoverOpen) {
      setCreditPopoverOpen(false);
      return;
    }
    setCreditLoading(true);
    setCreditInfo(null);
    try {
      const result = await checkCredit();
      setCreditInfo(result);
      setCreditPopoverOpen(true);
    } catch {
      // sessizce yut — endpoint local dev'de henüz mevcut olmayabilir
    } finally {
      setCreditLoading(false);
    }
  };

  // Buton alanı dışına tıklanınca popover'ı kapat
  useEffect(() => {
    if (!creditPopoverOpen) return undefined;
    const onClickOutside = (e) => {
      if (creditBtnRef.current && !creditBtnRef.current.contains(e.target)) {
        setCreditPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [creditPopoverOpen]);

  // Eğitimde kullanılan stili kendi bölümünde en üste taşı
  const sortedStyles = useMemo(() => {
    if (!selectedStyle || selectedStyle === CUSTOM_STYLE_ID || selectedStyle === COURSE_PROMPT_ID) {
      return styles;
    }
    const idx = styles.findIndex((s) => s.id === selectedStyle);
    if (idx <= 0) return styles;
    const arr = [...styles];
    arr.unshift(arr.splice(idx, 1)[0]);
    return arr;
  }, [styles, selectedStyle]);

  const sortedOrgStyles = useMemo(() => {
    if (!selectedOrgStyleId) return organizationStyles;
    const idx = organizationStyles.findIndex((s) => s.id === selectedOrgStyleId);
    if (idx <= 0) return organizationStyles;
    const arr = [...organizationStyles];
    arr.unshift(arr.splice(idx, 1)[0]);
    return arr;
  }, [organizationStyles, selectedOrgStyleId]);

  const isLoadingAny = loading || imageLoading;

  // Kadrosu olmayan eğitimde karakterli motorlar hiç listelenmez: kadro kurs
  // üretiminde BİR KEZ oluşuyor, sonradan üretilmiyor.
  const availableEngines = ENGINE_OPTIONS.filter(
    (e) => hasCast || !CHARACTER_ENGINES.includes(e.value)
  );
  const isCharacterEngine = CHARACTER_ENGINES.includes(selectedEngine);

  // Kadro kaybolursa (başka eğitim açıldı, istek başarısız oldu) seçili motor
  // listede olmayan bir değerde kalmasın — trigger boş metin gösterirdi.
  useEffect(() => {
    if (!hasCast && CHARACTER_ENGINES.includes(selectedEngine)) {
      setSelectedEngine(DEFAULT_ENGINE);
    }
  }, [hasCast, selectedEngine]);

  // Karakterler yalnızca Gemini sağlayıcılarında referans görsel olarak
  // iletilebiliyor (diğerleri sadece metin prompt alıyor), başka sağlayıcıda
  // kadro sessizce düşerdi. Backend de aynı kuralı zorluyor; bu onun görünen yüzü.
  useEffect(() => {
    if (!isCharacterEngine) return;
    setIsAutoProvider(true);
    setSelectedProviders([AUTO_PROVIDER]);
  }, [isCharacterEngine]);

  // Bölümü gizlemek yetmez: motor sonradan 7/8'e çevrilirse seçili kurum stili
  // state'te kalır ve formData'ya organizationStyleId olarak gider.
  useEffect(() => {
    if (isCharacterEngine && selectedOrgStyleId) setSelectedOrgStyleId(null);
  }, [isCharacterEngine, selectedOrgStyleId]);

  // The motion tab is only offered for a scene of narration and images: the
  // generated document covers the whole scene, so an avatar, a quiz or a text
  // box on it would end up hidden behind the animation with nothing saying so.
  const motionEligibility = motionSceneEligibility(
    store?.getState()?.undoGroup?.present,
    currentViewOrContained || navItemSelected
  );

  // AI Provider — rendered at the top of each tab (scene / image / sound)
  const providerSection = (
    <>
      {/* Image Provider */}
      <p className="infographic-modal__section-label" style={{ marginBottom: 6 }}>
        {i18n.t("FileModal.APIProviders.ImageStyles.AIProvider") || "AI Provider"}
      </p>
      <div
        className="infographic-modal__ai-mode-toggle"
        role="tablist"
        aria-label={i18n.t("FileModal.APIProviders.ImageStyles.AIProvider") || "AI Provider"}
      >
        <button
          type="button"
          role="tab"
          aria-selected={isAutoProvider}
          className={`infographic-modal__ai-mode-toggle-btn${
            isAutoProvider ? " infographic-modal__ai-mode-toggle-btn--active" : ""
          }`}
          onClick={selectAutoMode}
        >
          <span>{i18n.t("FileModal.APIProviders.ImageStyles.AutoProvider") || "Auto"}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={!isAutoProvider}
          className={`infographic-modal__ai-mode-toggle-btn${
            !isAutoProvider ? " infographic-modal__ai-mode-toggle-btn--active" : ""
          }`}
          onClick={selectCustomMode}
          disabled={isCharacterEngine}
        >
          <span>{i18n.t("FileModal.APIProviders.ImageStyles.CustomProvider") || "Custom"}</span>
        </button>
      </div>
      {isCharacterEngine && (
        <p className="infographic-modal__section-subtitle" style={{ marginTop: 6 }}>
          {i18n.t("InfographicEngine.provider_locked") ||
            "Karakterli anlatımda görseller karakter referanslarıyla üretildiği için sağlayıcı değiştirilemez."}
        </p>
      )}
      {!isAutoProvider && !isCharacterEngine && (
        <div className="infographic-modal__ai-provider-chips">
          {VISIBLE_IMAGE_STYLE_PROVIDER_OPTIONS.map((option) => {
            const isSelected = selectedProviders.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                className={`infographic-modal__ai-provider-chip${isSelected ? " infographic-modal__ai-provider-chip--active" : ""}`}
                onClick={() => toggleProvider(option.value)}
              >
                {isSelected && <LuCheck size={11} />}
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  // Model seçici — yalnızca motion sekmesinde. Diğer sekmelerdeki sağlayıcı
  // çipleri GÖRSEL üreticisini seçiyor; burada üretilen şey kod olduğu için
  // seçilen şey LLM. Otomatik = backend'in kendi varsayılanı.
  const motionModelSection = (
    <>
      <p className="infographic-modal__section-label" style={{ marginBottom: 6 }}>
        {i18n.t("FileModal.APIProviders.ImageStyles.AIProvider") || "AI Sağlayıcısı"}
      </p>
      <div
        className="infographic-modal__ai-mode-toggle"
        role="tablist"
        aria-label={i18n.t("FileModal.APIProviders.ImageStyles.AIProvider") || "AI Sağlayıcısı"}
      >
        <button
          type="button"
          role="tab"
          aria-selected={motionModel === null}
          className={`infographic-modal__ai-mode-toggle-btn${
            motionModel === null ? " infographic-modal__ai-mode-toggle-btn--active" : ""
          }`}
          onClick={() => setMotionModel(null)}
        >
          <span>{i18n.t("FileModal.APIProviders.ImageStyles.AutoProvider") || "Otomatik"}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={motionModel !== null}
          className={`infographic-modal__ai-mode-toggle-btn${
            motionModel !== null ? " infographic-modal__ai-mode-toggle-btn--active" : ""
          }`}
          onClick={() => setMotionModel((m) => m ?? MOTION_MODEL_OPTIONS[0].value)}
        >
          <span>{i18n.t("FileModal.APIProviders.ImageStyles.CustomProvider") || "Özel"}</span>
        </button>
      </div>
      {motionModel !== null && (
        <div className="infographic-modal__ai-provider-chips">
          {MOTION_MODEL_OPTIONS.map((option) => {
            const isSelected = motionModel === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className={`infographic-modal__ai-provider-chip${isSelected ? " infographic-modal__ai-provider-chip--active" : ""}`}
                // One model, not a set: the pipeline makes one call per stage
                // and a second choice would have nothing to do.
                onClick={() => setMotionModel(option.value)}
              >
                {isSelected && <LuCheck size={11} />}
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  // aideaEngine seçici — Sahne, Anlatımdan Oluştur ve Görsel Üret sekmelerinde ortak.
  // generate / generate-images (scene-builder) ve generate-image (tek görsel)
  // endpoint'lerinin hepsi aideaEngine'i kabul edip prompt'u engine'e göre seçiyor.
  const engineSection = (
    <>
      <p className="infographic-modal__section-label" style={{ marginBottom: 6 }}>
        {i18n.t("InfographicEngine.label") || "Aidea Engine"}{" "}
        <span className="infographic-modal__section-subtitle">
          {i18n.t("InfographicEngine.subtitle") || ""}
        </span>
      </p>
      <div className="infographic-modal__engine-select">
        <button
          type="button"
          className="infographic-modal__engine-trigger"
          onClick={() => setEngineOpen((o) => !o)}
          disabled={isLoadingAny || soundImagesLoading}
          aria-haspopup="listbox"
          aria-expanded={engineOpen}
        >
          {(() => {
            const active = availableEngines.find((e) => e.value === selectedEngine);
            return (
              <span className="infographic-modal__engine-trigger-text">
                <span className="infographic-modal__engine-name">
                  {i18n.t(active?.labelKey, active?.labelFallback)}
                </span>
                <span className="infographic-modal__engine-desc">
                  {i18n.t(active?.descKey, active?.descFallback)}
                </span>
              </span>
            );
          })()}
          <LuChevronDown
            size={16}
            className={`infographic-modal__engine-chevron${engineOpen ? " infographic-modal__engine-chevron--open" : ""}`}
          />
        </button>
        {engineOpen && (
          <ul className="infographic-modal__engine-list" role="listbox">
            {availableEngines.map((engine) => (
              <li
                key={engine.value}
                role="option"
                aria-selected={selectedEngine === engine.value}
                className={`infographic-modal__engine-option${selectedEngine === engine.value ? " infographic-modal__engine-option--active" : ""}`}
                onClick={() => {
                  setSelectedEngine(engine.value);
                  setEngineOpen(false);
                }}
              >
                <span className="infographic-modal__engine-option-head">
                  <span className="infographic-modal__engine-name">
                    {i18n.t(engine.labelKey, engine.labelFallback)}
                  </span>
                  {selectedEngine === engine.value && <LuCheck size={14} />}
                </span>
                <span className="infographic-modal__engine-desc">
                  {i18n.t(engine.descKey, engine.descFallback)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );

  const styleSection = (
    <>
      {/* Organization Style — karakterli motorlarda gizli: kadro referans
          sayfaları kursun kendi stiliyle üretildi, araya bir kurum stili
          girince yeni sahne kadroyla aynı görünmez. Console'daki VisualStyle
          ile aynı kural. */}
      {!isCharacterEngine && (orgStylesLoading || organizationStyles.length > 0) && (
        <>
          <p className="infographic-modal__section-label">
            {i18n.t("OrganizationStyle") || "Organization Style"}
          </p>
          {orgStylesLoading ? (
            <div className="infographic-modal__empty">
              {i18n.t("FileModal.APIProviders.ImageStyles.LoadingStyles") || "Loading styles..."}
            </div>
          ) : (
            <div className="infographic-modal__style-grid">
              {(showAllOrgStyles ? sortedOrgStyles : sortedOrgStyles.slice(0, 7)).map(
                (orgStyle) => (
                  <button
                    key={orgStyle.id}
                    type="button"
                    className={`infographic-modal__style-card${selectedOrgStyleId === orgStyle.id ? " infographic-modal__style-card--active" : ""}`}
                    onClick={() => {
                      setSelectedOrgStyleId((prev) => (prev === orgStyle.id ? null : orgStyle.id));
                      setSelectedStyle(null);
                    }}
                    disabled={isLoadingAny}
                  >
                    {orgStyle.previewImageUrl ? (
                      <img src={orgStyle.previewImageUrl} alt="" />
                    ) : (
                      <span className="infographic-modal__style-card-placeholder">
                        {orgStyle.name}
                      </span>
                    )}
                    <span>{orgStyle.name}</span>
                    {selectedOrgStyleId === orgStyle.id && (
                      <div className="infographic-modal__style-check">
                        <LuCheck size={12} />
                      </div>
                    )}
                  </button>
                )
              )}
            </div>
          )}
          {!orgStylesLoading && organizationStyles.length > 7 && (
            <button
              type="button"
              className="infographic-modal__style-more"
              onClick={() => setShowAllOrgStyles((v) => !v)}
            >
              <LuChevronDown
                size={14}
                style={{
                  transform: showAllOrgStyles ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
              {showAllOrgStyles
                ? i18n.t("FileModal.APIProviders.ImageStyles.LessStyles") || "Daha Az Stil"
                : i18n.t("FileModal.APIProviders.ImageStyles.MoreStyles") || "Daha Fazla Stil"}
            </button>
          )}
        </>
      )}

      {/* Image Style */}
      <p className="infographic-modal__section-label">
        {i18n.t("FileModal.APIProviders.ImageStyles.StyleLabel") || "Style"}
      </p>
      {stylesLoading ? (
        <div className="infographic-modal__empty">
          {i18n.t("FileModal.APIProviders.ImageStyles.LoadingStyles") || "Loading styles..."}
        </div>
      ) : (
        <>
          <div className="infographic-modal__style-grid">
            {coursePrompt && (
              <button
                type="button"
                className={`infographic-modal__style-card infographic-modal__style-card--course${isCoursePrompt ? " infographic-modal__style-card--active" : ""}`}
                onClick={() => {
                  setSelectedStyle(COURSE_PROMPT_ID);
                  setSelectedOrgStyleId(null);
                }}
                disabled={isLoadingAny}
                title={coursePrompt}
              >
                <div className="infographic-modal__style-icon" style={{ color: "#7c3aed" }}>
                  <LuCheck size={22} />
                </div>
                <span>{i18n.t("UsedPromptInCourse") || "Used in this course"}</span>
                {isCoursePrompt && (
                  <div className="infographic-modal__style-check">
                    <LuCheck size={12} />
                  </div>
                )}
              </button>
            )}

            {(showAllStyles ? sortedStyles : sortedStyles.slice(0, coursePrompt ? 5 : 6)).map(
              (style, index) => {
                const imageUrl = style.sampleImageUrl || style.thumbnail;
                const label = resolveStyleLabel(style, index + 1);
                return (
                  <button
                    key={style.id || index}
                    type="button"
                    className={`infographic-modal__style-card${selectedStyle === style.id ? " infographic-modal__style-card--active" : ""}`}
                    onClick={() => {
                      setSelectedStyle(style.id);
                      setSelectedOrgStyleId(null);
                    }}
                    disabled={isLoadingAny}
                  >
                    {imageUrl ? (
                      <img src={imageUrl} alt="" />
                    ) : (
                      <span className="infographic-modal__style-card-placeholder">{label}</span>
                    )}
                    <span>{label}</span>
                    {selectedStyle === style.id && (
                      <div className="infographic-modal__style-check">
                        <LuCheck size={12} />
                      </div>
                    )}
                  </button>
                );
              }
            )}

            <button
              type="button"
              className={`infographic-modal__style-card infographic-modal__style-card--custom${isCustomStyle ? " infographic-modal__style-card--active" : ""}`}
              onClick={() => {
                setSelectedStyle(CUSTOM_STYLE_ID);
                setSelectedOrgStyleId(null);
              }}
              disabled={isLoadingAny}
            >
              <div className="infographic-modal__style-icon">
                <LuPenLine size={26} />
              </div>
              <span>
                {i18n.t("FileModal.APIProviders.ImageStyles.CustomCardLabel") || "Custom"}
              </span>
              {isCustomStyle && (
                <div className="infographic-modal__style-check">
                  <LuCheck size={12} />
                </div>
              )}
            </button>
          </div>

          {sortedStyles.length > (coursePrompt ? 5 : 6) && (
            <button
              type="button"
              className="infographic-modal__style-more"
              onClick={() => setShowAllStyles((v) => !v)}
            >
              <LuChevronDown
                size={14}
                style={{
                  transform: showAllStyles ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
              {showAllStyles
                ? i18n.t("FileModal.APIProviders.ImageStyles.LessStyles") || "Daha Az Stil"
                : i18n.t("FileModal.APIProviders.ImageStyles.MoreStyles") || "Daha Fazla Stil"}
            </button>
          )}

          {isCustomStyle && (
            <div className="infographic-modal__custom-desc">
              <textarea
                value={customStyleDesc}
                onChange={(e) =>
                  e.target.value.length <= 2000 && setCustomStyleDesc(e.target.value)
                }
                placeholder={
                  i18n.t("FileModal.APIProviders.ImageStyles.CustomStylePlaceholder") ||
                  "Describe your image style..."
                }
                rows={4}
                maxLength={2000}
              />
              <div className="infographic-modal__custom-desc-count">
                {customStyleDesc.length}/2000
              </div>
            </div>
          )}
        </>
      )}
    </>
  );

  // Kütüphane seçici — modalın kendi içinde açılır (popup üstüne popup açılmaz).
  const libraryPanel = (
    <div
      className="infographic-modal__library"
      style={{
        marginTop: 10,
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        background: "#ffffff",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
          {i18n.t("SelectFromLibrary") || "Select from library"}
        </span>
        <div style={{ position: "relative", flex: 1 }}>
          <LuSearch
            size={14}
            style={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#9ca3af",
            }}
          />
          <input
            type="text"
            value={librarySearch}
            onChange={(e) => setLibrarySearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") loadLibraryImages(librarySearch);
            }}
            placeholder={i18n.t("search") || "Ara"}
            style={{
              width: "100%",
              fontSize: 12,
              border: "1px solid #e5e7eb",
              borderRadius: 999,
              padding: "6px 10px 6px 28px",
              color: "#374151",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => loadLibraryImages(librarySearch)}
          disabled={libraryLoading}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#7c3aed",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "4px 6px",
          }}
        >
          {i18n.t("search") || "Ara"}
        </button>
        <button
          type="button"
          onClick={() => setLibraryOpen(false)}
          title={i18n.t("close") || "Kapat"}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: 999,
            border: "1px solid #e5e7eb",
            background: "#ffffff",
            color: "#6b7280",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <LuX size={14} />
        </button>
      </div>
      <div
        style={{
          maxHeight: 280,
          overflowY: "auto",
          padding: 10,
        }}
      >
        {libraryLoading ? (
          <div style={{ textAlign: "center", padding: 24, color: "#9ca3af", fontSize: 13 }}>
            {i18n.t("loading") || "Loading..."}
          </div>
        ) : libraryError ? (
          <div style={{ textAlign: "center", padding: 24, color: "#ef4444", fontSize: 13 }}>
            {libraryError}
          </div>
        ) : libraryFiles.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24, color: "#9ca3af", fontSize: 13 }}>
            {i18n.t("no_files_found") || "No files found"}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
              gap: 8,
            }}
          >
            {libraryFiles.map((file) => {
              const adding = libraryAddingId === file.id;
              return (
                <button
                  key={file.id}
                  type="button"
                  onClick={() => handlePickFromLibrary(file)}
                  disabled={!!libraryAddingId}
                  title={file.name}
                  style={{
                    position: "relative",
                    padding: 0,
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    overflow: "hidden",
                    background: "#f9fafb",
                    cursor: libraryAddingId ? "default" : "pointer",
                    aspectRatio: "1 / 1",
                  }}
                >
                  <img
                    src={file.url}
                    alt={file.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  {adding && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255,255,255,0.7)",
                      }}
                    >
                      <LuRefreshCw
                        size={18}
                        style={{ color: "#7c3aed", animation: "infog-spin 0.8s linear infinite" }}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const visualUploadBar = (disabled) => (
    <div className="infographic-modal__visuals">
      <div className="infographic-modal__visuals-header">
        <p className="infographic-modal__section-label" style={{ margin: 0 }}>
          {i18n.t("ReferenceImages") || "Reference Images"}
          <span className="infographic-modal__visuals-count">
            {courseVisualsLoading ? "…" : `${selectedVisuals.length}/${maxVisuals}`}
          </span>
        </p>
      </div>
      {courseVisualsLoading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            minHeight: 110,
            marginTop: 8,
            border: "2px dashed #e2e8f0",
            borderRadius: 10,
            color: "#64748b",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <style>{`@keyframes infog-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              border: "3px solid #ede9fe",
              borderTopColor: "#7c3aed",
              animation: "infog-spin 0.8s linear infinite",
            }}
          />
          {i18n.t("LoadingCourseReferenceImages") || "Eğitimin referans görselleri yükleniyor..."}
        </div>
      ) : libraryOpen ? (
        libraryPanel
      ) : (
        selectedVisuals.length < maxVisuals && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
            {/* Sol kart: bilgisayardan yükle + sürükle-bırak */}
            <label
              onDragOver={(e) => {
                e.preventDefault();
                if (!disabled) setRefDragActive(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setRefDragActive(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setRefDragActive(false);
                if (disabled) return;
                handleVisualsDrop(e.dataTransfer?.files);
              }}
              style={{
                flex: "1 1 200px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                minHeight: 110,
                padding: 16,
                border: `2px dashed ${refDragActive ? "#7c3aed" : "#cbd5e1"}`,
                borderRadius: 10,
                background: refDragActive ? "#f5f3ff" : "#fff",
                cursor: disabled ? "default" : "pointer",
                color: refDragActive ? "#7c3aed" : "#64748b",
                fontSize: 13,
                fontWeight: 600,
                textAlign: "center",
                transition: "border-color 0.15s, background 0.15s, color 0.15s",
              }}
            >
              <LuImage size={24} />
              {i18n.t("ReplaceProduct.UploadHint") ||
                "Upload image or drag-and-drop (PNG, JPG, JPEG)"}
              <input
                ref={visualsInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple
                style={{ display: "none" }}
                disabled={disabled}
                onChange={handleVisualsChange}
              />
            </label>
            {/* Sağ kart: kütüphaneden seç */}
            <button
              type="button"
              onClick={openLibrary}
              disabled={disabled}
              style={{
                flex: "1 1 200px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                minHeight: 110,
                padding: 16,
                border: "2px dashed #cbd5e1",
                borderRadius: 10,
                background: "#fff",
                cursor: disabled ? "default" : "pointer",
                color: "#64748b",
                fontSize: 13,
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              <LuLibrary size={24} />
              {i18n.t("ReplaceProduct.PickFromLibrary") || "Pick from library"}
            </button>
          </div>
        )
      )}
      {!courseVisualsLoading && selectedVisuals.length > 0 && (
        <div className="infographic-modal__visuals-list">
          {selectedVisuals.map((file, i) => (
            <div key={i} className="infographic-modal__visuals-item">
              <img src={URL.createObjectURL(file)} alt={file.name} />
              <button type="button" onClick={() => removeVisual(i)} disabled={disabled}>
                ×
              </button>
              <div style={{ position: "relative", marginTop: 4 }}>
                <input
                  type="text"
                  maxLength={60}
                  value={visualTags?.[i] ?? ""}
                  onChange={(e) => setVisualTag(i, e.target.value)}
                  placeholder={i18n.t("AddTagPlaceholder") || "Tag ekle..."}
                  disabled={disabled}
                  style={{
                    width: "100%",
                    fontSize: 12,
                    border: "1px solid #e5e7eb",
                    borderRadius: 4,
                    padding: "4px 26px 4px 6px",
                    color: "#374151",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    right: 5,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: 10,
                    color: "#9ca3af",
                    pointerEvents: "none",
                  }}
                >
                  {(visualTags?.[i] ?? "").length}/60
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const isImageTab = activeTab === "image";
  const isSoundTab = activeTab === "sound";
  const isMotionTab = activeTab === "motion";

  const activeLoading = isMotionTab
    ? motionLoading
    : isSoundTab
      ? soundImagesLoading
      : isImageTab
        ? imageLoading
        : loading;
  const activeDisabled = isMotionTab
    ? motionLoading || (!soundNarrationText && !sceneAudio)
    : isSoundTab
      ? soundImagesLoading || (!soundNarrationText && !sceneAudio)
      : isImageTab
        ? imageLoading || !imageText.trim()
        : loading || !narrationText.trim();
  const activeHandler = isMotionTab
    ? handleMotionize
    : isSoundTab
      ? handleGenerateFromSound
      : isImageTab
        ? handleGenerateImage
        : handleGenerate;

  // Same focus mode as the other tabs: once generation starts (motionLoading)
  // or a result is waiting (motionPendingApproval) the form collapses to a
  // spinner, then to the preview with Onayla / Vazgeç.
  const motionFocusMode = isMotionTab && (motionLoading || !!motionPendingApproval);

  // "Yeni Sahne Oluştur" tab'inde üretim başlayınca (loading) veya sonuç onay
  // bekliyorken (scenePendingApproval) form gizlenir; modal küçülür ve ortada
  // spinner → ardından üretilen görseller + Onayla/Vazgeç gösterilir.
  const sceneFocusMode = activeTab === "scene" && (loading || !!scenePendingApproval);

  const sceneApprovalImages = Object.values(scenePendingApproval?.result?.filesUploaded || {})
    .map((f) => f?.url)
    .filter(Boolean);

  // "Anlatımdan Oluştur" tab'inde de aynı focus mode: üretim başlayınca
  // (soundImagesLoading) veya onay bekliyorken (soundPendingApproval) form
  // gizlenir; modal küçülür ve ortada spinner → ardından görseller + Onayla/Vazgeç.
  const soundFocusMode = isSoundTab && (soundImagesLoading || !!soundPendingApproval);

  const soundApprovalImages = Object.values(soundPendingApproval?.result?.filesUploaded || {})
    .map((f) => f?.url)
    .filter(Boolean);

  const soundFocusView = (
    <div
      className="infographic-modal__focus"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 240,
        padding: "16px 8px",
        gap: 16,
      }}
    >
      <style>{`@keyframes infog-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      {soundImagesLoading ? (
        <>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              border: "4px solid #ede9fe",
              borderTopColor: "#7c3aed",
              animation: "infog-spin 0.8s linear infinite",
            }}
          />
          <p style={{ margin: 0, color: "#64748b", fontWeight: 600 }}>
            {i18n.t("GeneratingScene") || i18n.t("Generating") || "Generating images..."}
          </p>
        </>
      ) : (
        <>
          <p
            className="infographic-modal__section-label"
            style={{ textAlign: "center", margin: 0 }}
          >
            {i18n.t("ReviewGeneratedImages") || "Generated images"}
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              justifyContent: "center",
            }}
          >
            {soundApprovalImages.map((url) => (
              <div key={url} style={{ position: "relative" }}>
                <img
                  src={url}
                  alt=""
                  style={{
                    width: 280,
                    height: "auto",
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    display: "block",
                  }}
                />
                <button
                  type="button"
                  title={i18n.t("View fullscreen")}
                  onClick={() => setLightboxUrl(url)}
                  style={{
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                    background: "rgba(0,0,0,0.55)",
                    border: "none",
                    borderRadius: "50%",
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    cursor: "pointer",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <LuZoomIn size={14} />
                </button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="app-modal__btn app-modal__btn--ghost"
              onClick={handleDiscardSoundImages}
            >
              {i18n.t("Discard") || "Discard"}
            </button>
            <button
              type="button"
              className="app-modal__btn app-modal__btn--primary"
              onClick={handleApproveSoundImages}
            >
              {i18n.t("Approve") || "Onayla"}
            </button>
          </div>
        </>
      )}
    </div>
  );

  // "İnfografik Görsel Oluştur" tab'i de aynı focus mode: üretim (imageLoading)
  // veya onay bekliyorken (imagePendingApproval) form gizlenir; ortada spinner →
  // ardından görsel + Onayla/Vazgeç.
  const imageFocusMode = isImageTab && (imageLoading || !!imagePendingApproval);

  const imageApprovalUrl = extractImageUrl(imagePendingApproval?.result);

  const imageFocusView = (
    <div
      className="infographic-modal__focus"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 240,
        padding: "16px 8px",
        gap: 16,
      }}
    >
      <style>{`@keyframes infog-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      {imageLoading ? (
        <>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              border: "4px solid #ede9fe",
              borderTopColor: "#7c3aed",
              animation: "infog-spin 0.8s linear infinite",
            }}
          />
          <p style={{ margin: 0, color: "#64748b", fontWeight: 600 }}>
            {i18n.t("GeneratingImage") || i18n.t("Generating") || "Generating image..."}
          </p>
        </>
      ) : (
        <>
          <p
            className="infographic-modal__section-label"
            style={{ textAlign: "center", margin: 0 }}
          >
            {i18n.t("ReviewGeneratedImages") || "Generated images"}
          </p>
          {imageApprovalUrl && (
            <div style={{ position: "relative", width: "100%", maxWidth: 560 }}>
              <img
                src={imageApprovalUrl}
                alt=""
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                  display: "block",
                }}
              />
              <button
                type="button"
                title={i18n.t("View fullscreen")}
                onClick={() => setLightboxUrl(imageApprovalUrl)}
                style={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  background: "rgba(0,0,0,0.55)",
                  border: "none",
                  borderRadius: "50%",
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  cursor: "pointer",
                  backdropFilter: "blur(4px)",
                }}
              >
                <LuZoomIn size={14} />
              </button>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="app-modal__btn app-modal__btn--ghost"
              onClick={handleDiscardImage}
            >
              {i18n.t("Discard") || "Discard"}
            </button>
            <button
              type="button"
              className="app-modal__btn app-modal__btn--primary"
              onClick={handleApproveImage}
            >
              {i18n.t("Approve") || "Onayla"}
            </button>
          </div>
        </>
      )}
    </div>
  );

  const sceneFocusView = (
    <div
      className="infographic-modal__focus"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 240,
        padding: "16px 8px",
        gap: 16,
      }}
    >
      <style>{`@keyframes infog-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      {loading ? (
        <>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              border: "4px solid #ede9fe",
              borderTopColor: "#7c3aed",
              animation: "infog-spin 0.8s linear infinite",
            }}
          />
          <p style={{ margin: 0, color: "#64748b", fontWeight: 600 }}>
            {i18n.t("GeneratingScene") || i18n.t("Generating") || "Generating scene..."}
          </p>
        </>
      ) : (
        <>
          <p
            className="infographic-modal__section-label"
            style={{ textAlign: "center", margin: 0 }}
          >
            {`${i18n.t("ReviewGeneratedImages") || "Generated images"} — ${
              i18n.t("AsNewScene") || "yeni sahne"
            }`}
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              justifyContent: "center",
            }}
          >
            {sceneApprovalImages.map((url) => (
              <div key={url} style={{ position: "relative" }}>
                <img
                  src={url}
                  alt=""
                  style={{
                    width: 280,
                    height: "auto",
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    display: "block",
                  }}
                />
                <button
                  type="button"
                  title={i18n.t("View fullscreen")}
                  onClick={() => setLightboxUrl(url)}
                  style={{
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                    background: "rgba(0,0,0,0.55)",
                    border: "none",
                    borderRadius: "50%",
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    cursor: "pointer",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <LuZoomIn size={14} />
                </button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="app-modal__btn app-modal__btn--ghost"
              onClick={handleDiscardScene}
            >
              {i18n.t("Discard") || "Discard"}
            </button>
            <button
              type="button"
              className="app-modal__btn app-modal__btn--primary"
              onClick={handleApproveScene}
            >
              {i18n.t("Approve") || "Onayla"}
            </button>
          </div>
        </>
      )}
    </div>
  );

  // Sol altta kredi tahmini butonu + popover; sağda standart İptal/Oluştur.
  const creditFooter = (
    <>
      <style>{`@keyframes infog-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <div
        className="app-modal__footer"
        style={{ justifyContent: "space-between", alignItems: "center" }}
      >
        {/* Kredi tahmini butonu + popover */}
        <div ref={creditBtnRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={handleCreditCheck}
            disabled={creditLoading || activeDisabled}
            title={i18n.t("CreditEstimate.Tooltip")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "9px 16px",
              borderRadius: "12px",
              border: `1px solid ${creditPopoverOpen ? "#c4b5fd" : "#dce3f3"}`,
              background: creditPopoverOpen ? "#eef2ff" : "#f8fafc",
              color: creditPopoverOpen ? "#7c3aed" : "#64748b",
              fontSize: "14px",
              fontWeight: 600,
              cursor: creditLoading ? "wait" : "pointer",
              transition: "background 0.18s ease, color 0.18s ease, border-color 0.18s ease",
              outline: "none",
              whiteSpace: "nowrap",
            }}
          >
            {creditLoading ? (
              <LuRefreshCw size={14} style={{ animation: "infog-spin 0.8s linear infinite" }} />
            ) : (
              <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
            )}
            <span>
              {creditLoading ? i18n.t("CreditEstimate.Loading") : i18n.t("CreditEstimate.Button")}
            </span>
          </button>

          {creditPopoverOpen && creditInfo && (
            <div
              style={{
                position: "absolute",
                bottom: "calc(100% + 10px)",
                left: 0,
                background: "#ffffff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                padding: "16px 20px",
                minWidth: "270px",
                maxWidth: "340px",
                boxShadow: "0 4px 24px rgba(15, 23, 42, 0.12)",
                zIndex: 9999,
                fontSize: "13px",
                lineHeight: 1.6,
              }}
            >
              {/* Arrow */}
              <div
                style={{
                  position: "absolute",
                  bottom: -7,
                  left: 20,
                  width: 13,
                  height: 13,
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderTop: "none",
                  borderLeft: "none",
                  transform: "rotate(45deg)",
                }}
              />

              <div
                style={{
                  fontWeight: 700,
                  marginBottom: 12,
                  fontSize: "14px",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
                {i18n.t("CreditEstimate.Title")}
              </div>

              {/* Sağlayıcı bazlı maliyet satırları */}
              {creditInfo.providers &&
                creditInfo.providers.map((p, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      gap: 12,
                      marginBottom: 7,
                    }}
                  >
                    <span style={{ color: "#64748b", fontSize: "13px", flex: 1, lineHeight: 1.35 }}>
                      {p.label}
                    </span>
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "13px",
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.cost.toFixed(2)}&nbsp;
                      <span style={{ color: "#8b95af", fontWeight: 400 }}>
                        {i18n.t("ImageHistory.Credits")}
                      </span>
                    </span>
                  </div>
                ))}

              {/* Toplam satırı — yalnızca birden fazla sağlayıcı varsa */}
              {creditInfo.providers && creditInfo.providers.length > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 4,
                    paddingTop: 8,
                    borderTop: "1px solid #f1f5f9",
                    marginBottom: 6,
                  }}
                >
                  <span style={{ color: "#64748b", fontWeight: 600 }}>
                    {i18n.t("CreditEstimate.Cost")}
                  </span>
                  <span style={{ fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
                    {creditInfo.requiredCredits.toFixed(2)}&nbsp;
                    <span style={{ color: "#8b95af", fontWeight: 400 }}>
                      {i18n.t("ImageHistory.Credits")}
                    </span>
                  </span>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 14,
                  paddingBottom: 14,
                  borderBottom: "1px solid #f1f5f9",
                  marginTop: creditInfo.providers && creditInfo.providers.length === 1 ? 2 : 6,
                }}
              >
                <span style={{ color: "#64748b" }}>{i18n.t("CreditEstimate.Balance")}</span>
                <span style={{ fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
                  {creditInfo.availableCredits.toFixed(2)}&nbsp;
                  <span style={{ color: "#8b95af", fontWeight: 400 }}>
                    {i18n.t("ImageHistory.Credits")}
                  </span>
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 12px",
                  borderRadius: 8,
                  background: creditInfo.hasSufficientBalance
                    ? "rgba(124,58,237,0.08)"
                    : "rgba(239,68,68,0.08)",
                  color: creditInfo.hasSufficientBalance ? "#7c3aed" : "#ef4444",
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {creditInfo.hasSufficientBalance
                  ? `✓ ${i18n.t("CreditEstimate.Sufficient")}`
                  : `✗ ${i18n.t("CreditEstimate.Insufficient")}`}
              </div>
            </div>
          )}
        </div>

        {/* Standart İptal + Oluştur */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            onClick={onCancel || onClose}
            disabled={activeLoading}
          >
            {i18n.t("Cancel") || "Cancel"}
          </button>
          <button
            type="button"
            className="app-modal__btn app-modal__btn--primary"
            onClick={activeHandler}
            disabled={activeDisabled}
          >
            {activeLoading
              ? i18n.t("Generating") || "Generating..."
              : i18n.t("Generate") || "Generate"}
          </button>
        </div>
      </div>
    </>
  );

  // Başlık + inline sekmeler (AIVoiceTab/AddSoundModal ile aynı: başlık metninin
  // yanında sekmeler header'da yer alır).
  const titleWithTabs = (
    <div className="infographic-modal__title-with-tabs">
      <span className="infographic-modal__title-text">
        {i18n.t("InfographicScene") || "Infographic Scene"}
      </span>
      <div className="infographic-modal__tabs infographic-modal__tabs--inline">
        {[
          { id: "scene", label: i18n.t("GenerateNewScene") || "Generate New Scene" },
          {
            id: "image",
            label: i18n.t("GenerateInfographicImage") || "Generate Infographic Image",
          },
          { id: "sound", label: i18n.t("GenerateFromNarration") || "Generate from Narration" },
          // Offered only when the scene has narration — motionizing is defined
          // by syncing to that audio — and when the scene holds nothing but
          // that narration and its images.
          {
            id: "motion",
            label: i18n.t("MotionizeScene") || "Sahneden Motion Oluştur",
            needsAudio: true,
            // Shown but disabled rather than hidden: a tab that quietly is not
            // there teaches the user nothing, and the reason is worth saying.
            disabled: !motionEligibility.eligible,
            disabledReason: motionEligibility.blockers.length
              ? `${i18n.t("MotionizeBlocked") || "Bu sahnede"} ${motionEligibility.blockers
                  .map(blockerLabel)
                  .join(
                    ", "
                  )} ${i18n.t("MotionizeBlockedTail") || "var — sahnede yalnızca anlatım ve görsel olmalı"}`
              : null,
          },
        ]
          .filter((tab) => !tab.needsAudio || !!sceneAudio)
          .map((tab) => (
            <button
              key={tab.id}
              type="button"
              disabled={tab.disabled}
              title={tab.disabledReason || undefined}
              onClick={() => {
                if (!tab.disabled) setActiveTab(tab.id);
              }}
              className={`infographic-modal__tab${activeTab === tab.id ? " infographic-modal__tab--active" : ""}${
                tab.disabled ? " infographic-modal__tab--disabled" : ""
              }`}
            >
              <span className="infographic-modal__tab-label">{tab.label}</span>
            </button>
          ))}
      </div>
    </div>
  );

  return (
    <AppModal
      show={show}
      onHide={onClose}
      onClose={onClose}
      title={titleWithTabs}
      cancelLabel={i18n.t("Cancel") || "Cancel"}
      onCancel={onCancel || onClose}
      cancelDisabled={activeLoading}
      confirmLabel={
        activeLoading ? i18n.t("Generating") || "Generating..." : i18n.t("Generate") || "Generate"
      }
      onConfirm={activeHandler}
      confirmDisabled={activeDisabled}
      footer={
        sceneFocusMode || soundFocusMode || imageFocusMode || motionFocusMode ? null : creditFooter
      }
      className="infographicSceneModal"
      size="lg"
    >
      <div className="infographic-modal__body">
        {/* Sesten Görsel Üret Tab */}
        {isSoundTab &&
          (soundFocusMode ? (
            soundFocusView
          ) : (
            <>
              {visualUploadBar(soundImagesLoading)}
              {providerSection}
              {engineSection}
              <p className="infographic-modal__hint">
                {i18n.t("GenerateFromNarrationHint") ||
                  "(Existing scene visuals will be replaced by new ones. Use an empty scene if you don't want to lose current visuals.)"}
              </p>
              <p className="infographic-modal__section-label">
                {i18n.t("NarrationText") || "Narration Text"}
              </p>
              <textarea
                className="infographic-modal__textarea"
                value={soundNarrationText}
                readOnly
                rows={4}
                style={{
                  background: "#f9fafb",
                  color: "#6b7280",
                  cursor: "default",
                  resize: "none",
                }}
              />

              {/* Ses player */}
              {soundAudioUrl && (
                <audio controls src={soundAudioUrl} style={{ width: "100%", marginTop: 4 }} />
              )}

              {styleSection}
              {soundImagesError && <p className="infographic-modal__error">{soundImagesError}</p>}
            </>
          ))}

        {/* Görsel Üret Tab */}
        {/* Sahneyi Motionize Et Tab */}
        {isMotionTab &&
          (motionFocusMode ? (
            <div
              className="infographic-modal__focus"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 240,
                padding: "16px 8px",
                gap: 16,
              }}
            >
              <style>{`@keyframes infog-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
              {motionLoading ? (
                <>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      border: "4px solid #ede9fe",
                      borderTopColor: "#7c3aed",
                      animation: "infog-spin 0.8s linear infinite",
                    }}
                  />
                  <p style={{ margin: 0, color: "#64748b", fontWeight: 600 }}>
                    {i18n.t("MotionizeWorking") || "Sahne animasyona dönüştürülüyor..."}
                  </p>
                </>
              ) : (
                <>
                  <p
                    className="infographic-modal__section-label"
                    style={{ textAlign: "center", margin: 0 }}
                  >
                    {i18n.t("ReviewMotionScene") || "Üretilen motion sahne"}
                  </p>
                  {/* Played in its own sandbox, exactly as the MotionScene
                      plugin will play it once approved. */}
                  <iframe
                    title="motion-preview"
                    sandbox="allow-scripts"
                    srcDoc={motionPreviewSrc ? undefined : motionPreviewHtml || ""}
                    src={motionPreviewSrc || undefined}
                    style={{
                      width: "100%",
                      maxWidth: 560,
                      aspectRatio: "16 / 9",
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      background: "#000",
                      display: "block",
                    }}
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      className="app-modal__btn app-modal__btn--ghost"
                      onClick={handleDiscardMotion}
                    >
                      {i18n.t("Discard") || "Discard"}
                    </button>
                    <button
                      type="button"
                      className="app-modal__btn app-modal__btn--primary"
                      onClick={handleApproveMotion}
                    >
                      {i18n.t("Approve") || "Onayla"}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div>
              {/* The LLM picker, not the image-provider chips: what this tab
                  produces is code, so an image generator has no part in it. */}
              {motionModelSection}
              <p className="infographic-modal__section-label" style={{ marginBottom: 6 }}>
                {i18n.t("SceneNarration") || "Sahne anlatımı"}
              </p>
              <textarea
                className="infographic-modal__textarea"
                value={soundNarrationText}
                onChange={(e) => setSoundNarrationText(e.target.value)}
                rows={4}
              />
              {soundAudioUrl && (
                <audio controls src={soundAudioUrl} style={{ width: "100%", marginTop: 4 }} />
              )}
              <p className="infographic-modal__hint">
                {i18n.t("MotionizeHint") ||
                  "Sahnenin anlatımı ve görselleri kullanılarak tamamen kodla çizilen hareketli bir sahne üretilir. Görseller birebir kopyalanmaz; konuyu ve görsel dünyayı anlatmak için örnek alınır. Hareketler anlatımın kelime zamanlamalarına oturur."}
              </p>
              {getSceneImageUrls().length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <p className="infographic-modal__section-label" style={{ marginBottom: 6 }}>
                    {i18n.t("MotionizeReferences") || "Referans görseller"} (
                    {getSceneImageUrls().length})
                  </p>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {getSceneImageUrls().map((url) => (
                      <img
                        key={url}
                        src={url}
                        alt=""
                        onClick={() => setLightboxUrl(url)}
                        style={{
                          width: 54,
                          height: 54,
                          objectFit: "cover",
                          borderRadius: 4,
                          border: "1px solid #e2e8f0",
                          cursor: "pointer",
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
              {motionError && <div className="infographic-modal__error">{motionError}</div>}
            </div>
          ))}

        {isImageTab &&
          (imageFocusMode ? (
            imageFocusView
          ) : (
            <>
              {visualUploadBar(imageLoading)}
              {providerSection}
              {engineSection}
              <p className="infographic-modal__hint">
                {i18n.t("GenerateImageHint") ||
                  "(Adds only one infographic image to the current scene)"}
              </p>
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                <p className="infographic-modal__section-label" style={{ margin: 0 }}>
                  {i18n.t("ImageText") || "Image Text"}
                </p>
                <button
                  type="button"
                  onClick={() => handleFetchSceneText(setImageText)}
                  disabled={imageLoading || fetchingSceneText}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "#7c3aed",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: imageLoading || fetchingSceneText ? "default" : "pointer",
                  }}
                >
                  {fetchingSceneText
                    ? i18n.t("Loading") || "Loading..."
                    : i18n.t("FetchSceneText") || "Fetch scene text"}
                </button>
              </div>
              <textarea
                className="infographic-modal__textarea"
                placeholder={
                  i18n.t("WriteImageText") || "Write the text you want to turn into an image..."
                }
                value={imageText}
                onChange={(e) => setImageText(e.target.value)}
                rows={3}
                disabled={imageLoading}
              />
              {styleSection}
              {imageError && <p className="infographic-modal__error">{imageError}</p>}
            </>
          ))}

        {/* Sahne Üret Tab */}
        {activeTab === "scene" &&
          (sceneFocusMode ? (
            sceneFocusView
          ) : (
            <>
              {visualUploadBar(loading)}
              {providerSection}
              {engineSection}
              <p className="infographic-modal__hint">
                {i18n.t("GenerateSceneHint") ||
                  "(Adds a fully ready new scene after the selected scene)"}
              </p>
              {/* Narration */}
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                <p className="infographic-modal__section-label" style={{ margin: 0 }}>
                  {i18n.t("NarrationText") || "Narration Text"}
                </p>
                <button
                  type="button"
                  onClick={() => handleFetchSceneText(setNarrationText)}
                  disabled={loading || fetchingSceneText}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "#7c3aed",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: loading || fetchingSceneText ? "default" : "pointer",
                  }}
                >
                  {fetchingSceneText
                    ? i18n.t("Loading") || "Loading..."
                    : i18n.t("FetchSceneText") || "Fetch scene text"}
                </button>
              </div>
              <textarea
                className="infographic-modal__textarea"
                placeholder={i18n.t("WriteYourNarration") || "Write your narration..."}
                value={narrationText}
                onChange={(e) => setNarrationText(e.target.value)}
                rows={4}
                disabled={loading}
              />

              {/* Voice — AIVoiceTab ile aynı pattern */}
              <div className="infographic-modal__voice-profile">
                <div className="infographic-modal__voice-row">
                  <div
                    className="infographic-modal__voice-avatar"
                    style={
                      selectedVoice
                        ? genderAvatarStyle(selectedVoice.gender || selectedGender)
                        : undefined
                    }
                  >
                    {(selectedVoice?.name || profileVoiceName || "?")[0].toUpperCase()}
                  </div>
                  <div className="infographic-modal__voice-details">
                    <div className="infographic-modal__voice-title-row">
                      <span className="infographic-modal__voice-name">
                        {selectedVoice?.name || profileVoiceName || "-"}
                      </span>
                      {(selectedService || profileService) && (
                        <span className="infographic-modal__voice-pill">
                          {selectedService || profileService}
                        </span>
                      )}
                      {(selectedLanguage || profileLanguage) && (
                        <span className="infographic-modal__voice-pill">
                          {selectedLanguage || profileLanguage}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="infographic-modal__voice-action"
                    onClick={() => setProfileOpen((prev) => !prev)}
                  >
                    {i18n.t("ChangeNarrator") || "Change narrator"}
                    <span className="infographic-modal__voice-action-icon">
                      {profileOpen ? "▲" : "▼"}
                    </span>
                  </button>
                </div>

                {profileOpen && (
                  <div className="infographic-modal__voice-panel">
                    <div className="infographic-modal__voice-filters">
                      {/* Sağlayıcı — chip/pill (AIVoiceTab tasarımı) */}
                      <div className="infographic-modal__voice-filter">
                        <div className="infographic-modal__voice-filter-label">
                          {i18n.t("Provider") || "Provider"}
                        </div>
                        <div className="infographic-modal__voice-filter-chips">
                          {[
                            { value: "", label: i18n.t("AllProviders") || "All" },
                            { value: "elevenlabs", label: i18n.t("ElevenLabs") || "ElevenLabs" },
                            { value: "google", label: i18n.t("Google") || "Google" },
                            { value: "azure", label: i18n.t("Azure") || "Azure" },
                          ].map((p) => (
                            <button
                              key={p.value || "all"}
                              type="button"
                              className={`infographic-modal__voice-filter-chip infographic-modal__voice-filter-chip--provider${
                                selectedService === p.value
                                  ? " infographic-modal__voice-filter-chip--on"
                                  : ""
                              }`}
                              onClick={() => handleServiceChange(p.value)}
                              disabled={loading}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {selectedService &&
                        selectedService !== "elevenlabs" &&
                        selectedService !== "google" && (
                          <div className="infographic-modal__voice-filter">
                            <div className="infographic-modal__voice-filter-label">
                              {i18n.t("language") || "Language"}
                            </div>
                            <div className="infographic-modal__voice-select-input">
                              <select
                                value={selectedLanguage}
                                onChange={(e) => {
                                  setSelectedLanguage(e.target.value);
                                  setCurrentPage(1);
                                  if (selectedService)
                                    loadVoices(selectedService, e.target.value, selectedGender, 1);
                                }}
                                disabled={!selectedService || loading}
                              >
                                <option value="">
                                  {i18n.t("SelectLanguage") || "Select a language"}
                                </option>
                                {(LANGUAGE_OPTIONS[selectedService] || []).map((l) => (
                                  <option key={l.value} value={l.value}>
                                    {l.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}

                      {/* Cinsiyet — chip/pill (AIVoiceTab tasarımı) */}
                      <div className="infographic-modal__voice-filter">
                        <div className="infographic-modal__voice-filter-label">
                          {i18n.t("Gender") || "Gender"}
                        </div>
                        <div className="infographic-modal__voice-filter-chips">
                          {[
                            { value: "", label: i18n.t("AllGenders") || "All" },
                            { value: "female", label: i18n.t("Female") || "Female" },
                            { value: "male", label: i18n.t("Male") || "Male" },
                          ].map((g) => (
                            <button
                              key={g.value || "all"}
                              type="button"
                              className={`infographic-modal__voice-filter-chip${
                                selectedGender === g.value
                                  ? " infographic-modal__voice-filter-chip--on"
                                  : ""
                              }`}
                              onClick={() => {
                                setSelectedGender(g.value);
                                setCurrentPage(1);
                                if (selectedService)
                                  loadVoices(selectedService, selectedLanguage, g.value, 1);
                              }}
                              disabled={loading}
                            >
                              {g.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {
                      <div className="infographic-modal__voice-list">
                        {loadingVoices ? (
                          <div className="infographic-modal__empty">
                            {i18n.t("Loading") || "Loading"}...
                          </div>
                        ) : voices.length === 0 ? (
                          <div className="infographic-modal__empty">
                            {i18n.t("NoVoicesFound") || "No voices found"}
                          </div>
                        ) : (
                          <>
                            {voices.map((voice) => {
                              const hasSample = voice.sampleUrlsByLanguage
                                ? Object.keys(voice.sampleUrlsByLanguage).length > 0
                                : !!(voice.sampleUrl || voice.audioSampleUrl || voice.previewUrl);
                              const isActive = selectedVoice?.id === voice.id;
                              return (
                                <div
                                  key={voice.id}
                                  className={`infographic-modal__voice-card${isActive ? " infographic-modal__voice-card--active" : ""}`}
                                  onClick={() => {
                                    setSelectedVoice(voice);
                                    // "Tümü" (All) filtresindeyken provider boş; üretim
                                    // için seçilen sesin gerçek sağlayıcısını set et.
                                    if (!selectedService) {
                                      const svc = voiceToService(voice);
                                      if (svc) setSelectedService(svc);
                                    }
                                    if (voice.language) setSelectedLanguage(voice.language);
                                    if (voice.gender)
                                      setSelectedGender(normalizeGender(voice.gender));
                                  }}
                                >
                                  {/* Sample oynat — cinsiyete göre renkli (AIVoiceTab) */}
                                  <button
                                    type="button"
                                    className="infographic-modal__voice-card-play"
                                    style={genderAvatarStyle(voice.gender)}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSamplePlay(voice);
                                    }}
                                    disabled={!hasSample}
                                  >
                                    {samplePlayingId === voice.id ? (
                                      <LuPause size={14} />
                                    ) : (
                                      <LuPlay size={14} />
                                    )}
                                  </button>
                                  <div className="infographic-modal__voice-card-body">
                                    <div className="infographic-modal__voice-card-name">
                                      {voice.name}
                                    </div>
                                    <div className="infographic-modal__voice-card-meta">
                                      {voice.gender && (
                                        <span>
                                          {normalizeGender(voice.gender) === "male"
                                            ? i18n.t("Male") || "Male"
                                            : i18n.t("Female") || "Female"}
                                        </span>
                                      )}
                                      {voice.servicename && <span>{voice.servicename}</span>}
                                    </div>
                                  </div>
                                  {isActive && (
                                    <span className="infographic-modal__voice-card-check">
                                      <LuCheck size={14} />
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                            {totalPages > 1 && (
                              <div className="infographic-modal__voice-pagination">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                  <button
                                    key={page}
                                    type="button"
                                    className={`infographic-modal__voice-pagination-btn${currentPage === page ? " infographic-modal__voice-pagination-btn--active" : ""}`}
                                    onClick={() => {
                                      setCurrentPage(page);
                                      loadVoices(
                                        selectedService,
                                        selectedLanguage,
                                        selectedGender,
                                        page
                                      );
                                    }}
                                  >
                                    {page}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    }
                  </div>
                )}
              </div>

              {styleSection}

              {error && <p className="infographic-modal__error">{error}</p>}
            </>
          ))}
      </div>
      <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </AppModal>
  );
};

export default InfographicSceneModal;
